import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

// ─── Lightweight i18n (EN/ID) ──────────────────────────────────────────────
// Dependency-free, mirroring the mobile app's typed-strings approach. A flat
// key -> string dictionary per language, a `t(key, vars)` lookup with {{var}}
// interpolation, persisted in localStorage. Indonesian is the default; missing
// keys fall back to Indonesian, then to the key itself. Keys are added per page
// as they're migrated.

export type Lang = 'id' | 'en';
const STORAGE_KEY = 'residenone:lang';

type Dict = Record<string, string>;

const ID: Dict = {
  // common
  'common.account': 'Akun',
  'common.rowsPerPage': 'Baris per halaman',
  'common.displayedRows': '{{from}}–{{to}} dari {{count}}',
  'common.editProfile': 'Ubah profil',
  'common.manageAccount': 'Kelola akun Clerk',
  'common.changePassword': 'Ubah kata sandi',
  'common.logout': 'Keluar',
  'common.openNav': 'Buka navigasi',
  // nav groups + leaves
  'nav.dashboard': 'Dashboard',
  'nav.peopleProperty': 'Warga & properti',
  'nav.units': 'Unit',
  'nav.residents': 'Warga',
  'nav.billing': 'Tagihan',
  'nav.fees': 'Iuran',
  'nav.invoices': 'Tagihan',
  'nav.reports': 'Laporan',
  'nav.amenities': 'Fasilitas',
  'nav.facilities': 'Fasilitas',
  'nav.bookings': 'Pemesanan',
  'nav.community': 'Komunitas',
  'nav.announcements': 'Pengumuman',
  'nav.complaints': 'Keluhan',
  'nav.paymentReminders': 'Pengingat pembayaran',
  'nav.securityOps': 'Keamanan & operasi',
  'nav.operations': 'Operasi',
  'nav.platform': 'Platform',
  'nav.developers': 'Developer',
  'nav.clusters': 'Cluster',
  'nav.archived': 'Arsip',
  'search.placeholder': 'Cari di platform…',
  'search.minimum': 'Ketik minimal 2 huruf',
  'search.noResults': 'Tidak ada hasil',
  'search.pages': 'Halaman',
  'search.developers': 'Developer',
  'search.clusters': 'Cluster',
  'search.members': 'Tim pengelola',
  'nav.developerManagers': 'Manajer developer',
  'nav.clusterAdmins': 'Admin cluster',
  'nav.security': 'Keamanan',
  'nav.residentApp': 'Aplikasi warga',
  'nav.branding': 'Branding',
  // dashboard
  'dash.greetingMorning': 'Selamat pagi',
  'dash.greetingAfternoon': 'Selamat siang',
  'dash.greetingEvening': 'Selamat malam',
  'dash.health.rate': 'Tingkat penagihan',
  'dash.health.rateMonth': '{{month}}, sejauh ini',
  'dash.health.arrears': 'Tunggakan',
  'dash.health.arrearsHouses': '{{count}} rumah menunggak',
  'dash.health.noArrears': 'Tidak ada tunggakan',
  'dash.health.seeReport': 'Lihat laporan',
  'dash.health.adoption': 'Adopsi aplikasi',
  'dash.health.adoptionMissing': '{{count}} rumah belum terdaftar',
  'dash.health.adoptionAll': 'Semua rumah sudah terdaftar',
  'dash.health.bookings': 'Pemesanan menunggu',
  'dash.health.bookingsWaiting': 'Perlu disetujui',
  'dash.health.handle': 'Tangani',
  'dash.health.complaints': 'Keluhan baru',
  'dash.health.complaintsWaiting': 'Belum ditangani',
  'dash.health.nothingWaiting': 'Tidak ada yang menunggu',
  'dash.group.emergency': '{{count}} darurat aktif',
  'dash.group.emergencyDetail': 'Segera periksa di Operasi.',
  'dash.group.open': 'Buka',
  'dash.group.booking': 'Pemesanan {{facility}}',
  'dash.group.bookingWaiting': 'Menunggu persetujuan',
  'dash.group.approve': 'Setujui',
  'dash.group.details': 'Detail',
  'dash.group.complaints': '{{count}} keluhan baru',
  'dash.group.complaintsDetail': 'Terlama: {{title}} · {{age}}',
  'dash.group.arrears': '{{count}} rumah menunggak',
  'dash.group.arrearsSerious': '{{serious}} di antaranya ≥ 3 bulan · total Rp {{amount}}',
  'dash.group.arrearsAmount': 'Total Rp {{amount}}',
  'dash.group.remind': 'Kirim pengingat',
  'dash.pace.title': 'Laju penagihan bulan ini',
  'dash.pace.ahead': 'Hari ke-{{day}}: {{now}}% sudah dibayar, lebih cepat dari {{month}} ({{then}}% di hari yang sama).',
  'dash.pace.behind': 'Hari ke-{{day}}: {{now}}% sudah dibayar, lebih lambat dari {{month}} ({{then}}% di hari yang sama).',
  'dash.pace.noBills': 'Belum ada tagihan bulan ini.',
  'dash.pace.dayN': 'Hari ke-{{day}}',
  'dash.pace.thisMonth': '{{month}} (bulan ini)',
  'dash.pace.lastMonth': '{{month}} (bulan lalu)',
  'dash.feed.title': 'Aktivitas terbaru',
  'dash.feed.empty': 'Belum ada aktivitas dalam 2 minggu terakhir.',
  'dash.feed.payment': '{{unit}} membayar Rp {{amount}}',
  'dash.feed.signup': '{{name}} bergabung · {{unit}}',
  'dash.feed.signupPending': '{{name}} mendaftar · menunggu kode undangan',
  'dash.feed.complaint': 'Keluhan baru: {{title}}',
  'dash.feed.booking': 'Pemesanan baru: {{facility}}',
  'dash.ago.minutes': '{{count}} mnt lalu',
  'dash.ago.hours': '{{count}} jam lalu',
  'dash.ago.yesterday': 'kemarin',
  'dash.permissionsCount': '{{count}} izin',
  'dash.tileUnits': 'Unit',
  'dash.tileResidents': 'Warga',
  'dash.tileOpenInvoices': 'Tagihan terbuka',
  'dash.tilePendingBookings': 'Pemesanan tertunda',
  'dash.tileOpenComplaints': 'Keluhan terbuka',
  'dash.tileActiveEmergencies': 'Darurat aktif',
  'dash.collectionThisMonth': 'Penagihan bulan ini',
  'dash.amountCollected': 'Jumlah terkumpul',
  'dash.housesPaid': 'Rumah sudah bayar',
  'dash.collectedOutstanding': '{{pct}}% tertagih · Rp {{outstanding}} tertunggak',
  'dash.statsFailed': 'Angka dashboard gagal dimuat. Periksa koneksi lalu coba lagi.',
  'dash.billingFailed': 'Ringkasan penagihan bulan ini gagal dimuat.',
  'dash.retry': 'Coba lagi',
  'dash.tileUnavailable': 'Tidak tersedia',
  'dash.noBillsThisMonth': 'Belum ada tagihan yang diterbitkan bulan ini.',
  'dash.action.title': 'Perlu tindakan',
  'dash.action.subtitle': 'Yang paling mendesak dulu.',
  'dash.action.empty': 'Tidak ada yang menunggu. Kerja bagus.',
  'dash.action.today': 'Hari ini',
  'dash.action.daysAgo': '{{count}} hari lalu',
  'dash.action.emergencyDetail': 'Rumah {{unit}} · tangani di Operasi',
  'dash.action.arrearsTitle': 'Rumah {{unit}}',
  'dash.action.arrearsDetail': 'Menunggak {{count}} bulan · kirim pengingat',
  'dash.action.bookingDetail': 'Mulai {{when}} · perlu persetujuan',
  'dash.action.complaintDetail': 'Rumah {{unit}}',
  'dash.action.kind.MEDICAL': 'Darurat medis',
  'dash.action.kind.FIRE': 'Kebakaran',
  'dash.action.kind.SECURITY': 'Darurat keamanan',
  'dash.action.kind.OTHER': 'Darurat',
  'dash.action.type.EMERGENCY': 'Darurat',
  'dash.action.type.ARREARS': 'Tunggakan',
  'dash.action.type.COMPLAINT': 'Keluhan',
  'dash.action.type.BOOKING': 'Pemesanan',
  'dash.avgResolve': 'Rata-rata selesai dalam {{time}}',
  'dash.hours': '{{count}} jam',
  'dash.days': '{{count}} hari',
  'dash.complaintsThisMonth': 'Keluhan bulan ini',
  'dash.noComplaintsThisMonth': 'Belum ada keluhan masuk bulan ini.',
  'dash.complaintsDone': 'Selesai',
  'dash.complaintsOpen': 'Masih terbuka',
  'dash.complaintsBreakdown': '{{pct}}% selesai · {{waiting}} menunggu · {{inProgress}} sedang dikerjakan',
  'dash.emergencyBanner': '{{count}} peringatan darurat aktif. Segera tangani.',
  'dash.emergencyOpen': 'Buka Operasi',
  'rpt.headline': '{{collected}} terkumpul dari {{billed}}',
  'rpt.kpiCollected': 'Terkumpul',
  'rpt.kpiOfBilled': 'dari {{billed}} ditagih',
  'rpt.kpiSparkHint': 'Terkumpul per bulan',
  'rpt.kpiRate': 'Tingkat penagihan',
  'rpt.kpiTarget': 'Target {{pct}}%',
  'rpt.kpiHousesPaid': 'Rumah lunas',
  'rpt.kpiNotYetPaid': '{{count}} belum bayar',
  'rpt.kpiAllPaid': 'Semua sudah bayar',
  'rpt.kpiSeeUnits': 'Lihat per unit',
  'rpt.kpiArrears': 'Tunggakan hari ini',
  'rpt.kpiArrearsHouses': '{{count}} rumah menunggak',
  'rpt.kpiNoArrears': 'Tidak ada tunggakan',
  'rpt.kpiSeeArrears': 'Lihat daftarnya',
  'rpt.statusTip': '{{status}}: {{count}} tagihan · Rp {{amount}}',
  'rpt.statusTotal': '{{count}} tagihan',
  'rpt.statusNone': 'Belum ada tagihan pada periode ini.',
  'bill.seriesCollected': 'Terkumpul',
  'bill.seriesOwed': 'Belum dibayar',
  'bill.seriesRate': 'Tingkat penagihan',
  'bill.clickMonthHint': 'Klik salah satu bulan untuk membuka laporannya.',
  'rpt.stillOwed': 'Sisa belum dibayar: {{amount}}',
  'bill.target': 'Target {{pct}}%',
  'bill.story.onTarget': 'Penagihan stabil di rata-rata {{avg}}%, semua bulan di atas target {{target}}%.',
  'bill.story.belowTarget': 'Rata-rata penagihan {{avg}}%. {{below}} dari {{months}} bulan di bawah target {{target}}%.',
  'bill.story.arrears': '{{houses}} rumah masih menunggak dengan total Rp {{amount}}.',
  'bill.story.noArrears': 'Tidak ada tunggakan.',
  'bill.arrearsContext': 'Rp {{amount}} setara {{months}} bulan iuran seluruh cluster · {{houses}} rumah ({{share}}% dari semua rumah).',
  'bill.historyTitle': 'Riwayat penagihan 12 bulan',
  'bill.historySubtitle': 'Uang terkumpul dan sisa tagihan per bulan, dengan persentase tertagih.',
  'bill.trendTooltip': 'terkumpul Rp {{collected}}, sisa Rp {{outstanding}}',
  'bill.arrearsTitle': 'Tunggakan',
  'bill.arrearsCount': '{{count}} rumah',
  'bill.sendReminders': 'Kirim pengingat',
  'bill.aging1': '≤ 1 bulan',
  'bill.aging2': '2–3 bulan',
  'bill.aging3': '> 3 bulan',
  'bill.noArrears': 'Tidak ada tunggakan. Semua rumah lunas.',
  'bill.monthsBehind': 'Menunggak {{count}} bulan',
  'bill.since': 'Sejak {{month}}',
  'role.STAFF': 'Staf',
  'role.SECURITY': 'Satpam',
  'role.RESIDENT': 'Warga',
  // reports
  'rpt.title': 'Laporan',
  'rpt.subtitle': 'Penagihan IPL di seluruh cluster.',
  'rpt.sendReminders': 'Kirim pengingat pembayaran',
  'rpt.tabSummary': 'Ringkasan',
  'rpt.tabByUnit': 'Per unit',
  'rpt.period': 'Periode',
  'rpt.periodAll': 'Semua waktu',
  'rpt.periodCurrent': 'Bulan ini',
  'rpt.periodSelect': 'Pilih bulan…',
  'rpt.periodYear': 'Tahun tertentu…',
  'rpt.yearLabel': 'Tahun {{year}}',
  'rpt.downloadYear': 'Unduh laporan {{year}}',
  'rpt.downloadMonth': 'Unduh laporan {{month}}',
  'rpt.download': 'Unduh',
  'rpt.csvStatus': 'Status',
  'rpt.csvPaidOn': 'Tanggal bayar',
  'rpt.csvMonthTotal': 'Total',
  'rpt.csvHouse': 'Rumah',
  'rpt.csvBilled': 'Total tagihan (Rp)',
  'rpt.csvPaid': 'Terbayar (Rp)',
  'rpt.csvOwed': 'Sisa (Rp)',
  'rpt.csvTotal': 'Total terkumpul (Rp)',
  'rpt.csvPaidCell': 'Lunas',
  'rpt.csvUnpaidCell': 'Belum bayar',
  'bill.yearTitle': 'Penagihan tahun {{year}}',
  'bill.dailyTitle': 'Pembayaran harian, {{month}}',
  'bill.dailySummary': '{{count}} pembayaran masuk, total Rp {{amount}}.',
  'bill.dailyEmpty': 'Belum ada pembayaran di bulan ini.',
  'bill.dailyTooltip': '{{count}} pembayaran, Rp {{amount}}',
  'bill.dailyDue': 'Jatuh tempo {{day}}',
  'bill.dailyBeforeDue': 'Sebelum jatuh tempo',
  'bill.dailyAfterDue': 'Setelah jatuh tempo',
  'rpt.year': 'Tahun',
  'rpt.month': 'Bulan',
  'rpt.outstanding': 'Tertunggak',
  'rpt.invoicesByStatus': 'Tagihan menurut status, {{period}}',
  'rpt.statusDraft': 'Draf',
  'rpt.statusOpen': 'Belum bayar',
  'rpt.statusPaid': 'Lunas',
  'rpt.statusOverdue': 'Terlambat',
  'rpt.statusVoid': 'Batal',
  'rpt.last12Months': '12 bulan terakhir — {{unit}}',
  'rpt.legendPaid': 'Lunas',
  'rpt.legendUnpaid': 'Belum bayar',
  'rpt.legendNotBilled': 'Belum ditagih',
  'rpt.tipNotBilled': '{{month}}: Belum ditagih',
  'rpt.tipPaid': '{{month}}: Lunas {{amount}}',
  'rpt.tipPaidOn': '{{month}}: Lunas {{amount}} pada {{date}}',
  'rpt.tipUnpaid': '{{month}}: Belum bayar {{amount}}',
  'rpt.findUnit': 'Cari unit',
  'rpt.findUnitPlaceholder': 'mis. A24, B12…',
  'rpt.byUnitHelp': 'Klik baris untuk melihat 12 bulan terakhir pembayaran. Klik nama unit untuk riwayat tagihan lengkap.',
  'rpt.noUnitsMatch': 'Tidak ada unit yang cocok.',
  'rpt.colUnit': 'Unit',
  'rpt.colThisMonth': 'Bulan ini',
  'rpt.colMonthsBehind': 'Bulan tertunggak',
  'rpt.colOldestUnpaid': 'Tunggakan terlama',
  'rpt.monthsBehindValue': '{{count}} bln',
  'rpt.upToDate': 'Lunas',
  'rpt.collapse': 'tutup',
  'rpt.expand': 'buka',
  'rpt.unitInvoicesTitle': 'Unit {{unit}} — tagihan',
  'rpt.noUnitInvoices': 'Belum ada tagihan untuk unit ini.',
  'rpt.colMonth': 'Bulan',
  'rpt.colAmount': 'Jumlah',
  'rpt.colStatus': 'Status',
  'rpt.colPaidOn': 'Dibayar pada',
  // invoices
  'inv.title': 'Tagihan',
  'inv.subtitle': 'IPL warga dan biaya lainnya. Tandai lunas manual untuk transfer / tunai.',
  'inv.status': 'Status',
  'inv.filterAll': 'Semua',
  'inv.newInvoice': 'Tagihan baru',
  'inv.statusDraft': 'Draf',
  'inv.statusOpen': 'Belum bayar',
  'inv.statusPaid': 'Lunas',
  'inv.statusVoid': 'Batal',
  'inv.statusOverdue': 'Terlambat',
  'inv.emptyAll': 'Belum ada tagihan.',
  'inv.emptyFiltered': 'Tidak ada tagihan {{status}}.',
  'inv.colNumber': 'Nomor',
  'inv.colUnit': 'Unit',
  'inv.colTotal': 'Total (Rp)',
  'inv.colIssued': 'Terbit',
  'inv.colDue': 'Jatuh tempo',
  'inv.paidAmount': '{{amount}} dibayar',
  'inv.loadMore': 'Muat lebih banyak',
  'common.loadingEllipsis': 'Memuat…',
  'common.cancel': 'Batal',
  'common.saving': 'Menyimpan…',
  // invoice detail (show)
  'inv.backToList': 'Kembali ke tagihan',
  'inv.notFound': 'Tagihan tidak ditemukan.',
  'inv.sourceLabel': 'Sumber: {{source}}',
  'inv.markPaid': 'Tandai lunas',
  'inv.void': 'Batalkan',
  'inv.detailsSection': 'Detail',
  'inv.unitId': 'ID Unit',
  'inv.paidAt': 'Dibayar pada',
  'inv.voidedAt': 'Dibatalkan pada',
  'inv.voidReason': 'Alasan pembatalan',
  'inv.notes': 'Catatan',
  'inv.amountsSection': 'Jumlah (Rp)',
  'inv.subtotal': 'Subtotal',
  'inv.tax': 'Pajak',
  'inv.total': 'Total',
  'inv.paid': 'Dibayar',
  'inv.outstanding': 'Sisa',
  'inv.lineItems': 'Item baris',
  'inv.colDescription': 'Deskripsi',
  'inv.colQty': 'Jml',
  'inv.colUnitPrice': 'Harga satuan (Rp)',
  'inv.noLineItems': 'Tidak ada item.',
  'inv.markPaidTitle': 'Tandai tagihan lunas',
  'inv.amountPositive': 'Jumlah harus berupa angka positif.',
  'inv.recordedPaid': 'Tercatat Rp {{amount}} dibayar. Warga diberi tahu.',
  'inv.paymentMethod': 'Metode pembayaran',
  'inv.bankTransfer': 'Transfer bank',
  'inv.cash': 'Tunai',
  'inv.amount': 'Jumlah',
  'inv.outstandingAmount': 'Sisa: Rp {{amount}}',
  'inv.noteOptional': 'Catatan (opsional)',
  'inv.notePlaceholder': 'mis. ref transfer 20260522-BCA-XXXX',
  'inv.confirmPayment': 'Konfirmasi pembayaran',
  'inv.voidTitle': 'Batalkan tagihan',
  'inv.reasonRequired': 'Alasan wajib diisi.',
  'inv.invoiceVoided': 'Tagihan dibatalkan.',
  'inv.voidWarning': 'Pembatalan menyembunyikan tagihan dari warga dan mencegah pembayaran. Tidak dapat dibatalkan.',
  'inv.reason': 'Alasan',
  'inv.reasonPlaceholder': 'mis. duplikat dari INV-202605-ABCDEF01',
  'inv.voiding': 'Membatalkan…',
  // create
  'inv.createTitle': 'Tagihan baru',
  'inv.createSubtitle': 'Biaya manual untuk satu unit. Untuk jadwal IPL bulanan, gunakan batch yang dibuat otomatis (Fase 6d).',
  'inv.createUnitsError': 'Gagal memuat unit: {{error}}',
  'inv.createFieldUnit': 'Unit',
  'inv.createFieldDesc': 'Deskripsi',
  'inv.createDescHelper': 'Tampil sebagai item baris tunggal pada tagihan.',
  'inv.createFieldTax': 'Pajak (opsional)',
  'inv.createTaxHelper': 'Ditambahkan ke total tagihan.',
  'inv.createFieldIssueDate': 'Tanggal terbit',
  'inv.createFieldDueDate': 'Tanggal jatuh tempo',
  'inv.createFieldNotes': 'Catatan (opsional)',
  'inv.createNotesHelper': 'Terlihat oleh warga pada detail tagihan.',
  'inv.creating': 'Membuat…',
  'inv.createSubmit': 'Buat tagihan',
  'inv.createValidUnit': 'Pilih unit.',
  'inv.createValidDesc': 'Deskripsi wajib diisi.',
  'inv.createValidDueDate': 'Tanggal jatuh tempo wajib diisi.',
  'inv.createValidTax': 'Pajak harus nol atau angka positif.',
  // auth (login.tsx)
  'auth.brand': 'ResidenOne',
  'auth.subtitle': 'Panel admin cluster',
  'auth.emailRequired': 'Email wajib diisi.',
  'auth.password': 'Kata sandi',
  'auth.passwordRequired': 'Kata sandi wajib diisi.',
  'auth.signingIn': 'Masuk…',
  'auth.signIn': 'Masuk',
  'auth.staffOnly': 'Khusus staf cluster. Warga menggunakan aplikasi mobile.',
  'auth.loginFailed': 'Gagal masuk',
  // common additions
  'common.email': 'Email',
  'common.status': 'Status',
  'common.actions': 'Aksi',
  'common.colName': 'Nama',
  'common.done': 'Selesai',
  'common.loadMore': 'Muat lebih banyak',
  'common.all': 'Semua',
  'common.edit': 'Ubah',
  'common.delete': 'Hapus',
  'common.deleting': 'Menghapus…',
  'common.fullName': 'Nama lengkap',
  'common.save': 'Simpan',
  'common.create': 'Buat',
  // residents (residents/list.tsx)
  'res.title': 'Warga',
  'res.titleBoth': 'Warga & tim pengelola',
  'res.subtitleShort': 'Semua yang mendaftar di cluster ini. Warga tertunda sampai memasukkan kode undangan unitnya.',
  'res.subtitleTeam': 'Admin, staf, dan keamanan yang mengelola cluster ini.',
  'res.tabResidents': 'Warga',
  'res.tabTeam': 'Tim pengelola',
  'res.invite': 'Undang',
  'res.import.tab': 'Impor warga',
  'res.import.step1Help': 'Isi satu warga per baris: nama, email, dan unitnya. Contoh di dalamnya boleh dihapus.',
  'res.import.fileColumns': 'Kolom "Nama lengkap", "Email", atau "Nomor" tidak ditemukan. Pakai template agar judul kolomnya sesuai.',
  'res.import.run': 'Undang {{count}} warga',
  'res.import.sent': '{{count}} undangan dikirim.',
  'res.import.errName': 'Nama kosong',
  'res.import.errNameLong': 'Nama lebih dari {{max}} karakter',
  'res.import.errEmail': 'Email tidak valid',
  'res.import.errNoUnit': 'Nomor unit kosong',
  'res.import.errUnknownUnit': 'Unit {{unit}} tidak ada di cluster ini',
  'res.import.errEmailExists': 'Email ini sudah terdaftar',
  'res.import.errEmailDuplicate': 'Email sama dengan baris {{line}}',
  'res.filterAll': 'Semua',
  'res.search': 'Cari nama, email, atau unit',
  'res.showing': '{{shown}} dari {{total}} warga',
  'res.colResident': 'Warga',
  'res.colJoined': 'Terdaftar',
  'res.notLinked': 'Belum terhubung',
  'nav.peopleSummary': 'Ringkasan',
  'ppl.title': 'Ringkasan warga & properti',
  'ppl.subtitle': 'Unit, hunian, dan warga yang terdaftar di cluster.',
  'ppl.download': 'Unduh data warga',
  'ppl.kpiUnits': 'Total unit',
  'ppl.kpiOccupied': 'Unit dihuni',
  'ppl.kpiOccupiedSub': '{{vacant}} kosong',
  'ppl.kpiResidents': 'Warga aktif',
  'ppl.kpiPending': '{{count}} menunggu kode undangan',
  'ppl.kpiNoPending': 'Tidak ada yang menunggu',
  'ppl.kpiNoResident': 'Belum terdaftar',
  'ppl.kpiNoResidentSub': 'Rumah yang perlu diundang ke aplikasi',
  'ppl.signupsTitle': 'Pendaftaran warga, 12 bulan terakhir',
  'ppl.signupsSub': '{{count}} warga baru dalam 12 bulan terakhir.',
  'ppl.seriesJoined': 'Warga baru',
  'ppl.seriesTotal': 'Total terdaftar',
  'ppl.statusTitle': 'Status warga',
  'ppl.statusTotal': 'warga',
  'ppl.blocksTitle': 'Hunian per blok',
  'ppl.blockCounts': '{{units}} unit · {{residents}} warga',
  'ppl.missingTitle': 'Belum terdaftar',
  'ppl.missingAction': 'Kelola warga',
  'ppl.missingHelp': 'Rumah dihuni yang belum punya akun warga. Bagikan kode undangan ke penghuninya.',
  'ppl.missingNone': 'Semua rumah yang dihuni sudah punya warga terdaftar.',
  'ppl.csvBlock': 'Blok / Jalan',
  'ppl.csvNumber': 'Nomor',
  'ppl.csvUnit': 'Unit',
  'ppl.csvType': 'Tipe',
  'ppl.csvOccupancy': 'Status hunian',
  'ppl.csvName': 'Nama warga',
  'ppl.csvEmail': 'Email',
  'ppl.csvStatus': 'Status warga',
  'ppl.csvPrimary': 'Warga utama',
  'ppl.csvSince': 'Terdaftar sejak',
  'ppl.csvYes': 'Ya',
  'ppl.csvNo': 'Tidak',
  'res.subtitle': 'Semua yang mendaftar di cluster ini. Warga berstatus Tertunda hingga memasukkan kode undangan unit, lalu Aktif. Anda dapat menangguhkan akun untuk memblokir akses.',
  'res.statusFilter': 'Status',
  'res.statusAll': 'Semua status',
  'res.statusActive': 'Aktif',
  'res.statusPending': 'Tertunda (belum punya kode)',
  'res.statusSuspended': 'Ditangguhkan',
  'res.total': '{{count}} total',
  'res.emptyAll': 'Belum ada warga. Muncul setelah mendaftar di aplikasi.',
  'res.emptyFiltered': 'Tidak ada warga yang cocok dengan filter ini.',
  'res.colUnit': 'Unit',
  'res.mainUser': 'Pengguna utama',
  'res.reactivate': 'Aktifkan kembali',
  'res.suspend': 'Tangguhkan',
  'res.deleteTitle': 'Hapus {{name}}?',
  'res.deleteWarning': 'Ini menghapus mereka dari daftar dan mencabut akses. Tagihan dan riwayat tetap tersimpan. Gunakan Tangguhkan untuk pemblokiran yang dapat dibatalkan.',
  'res.chipActive': 'Aktif',
  'res.chipSuspended': 'Ditangguhkan',
  'res.chipPending': 'Tertunda',
  'res.deletedInfo': 'Warga {{name}} dihapus.',
  'res.alreadyRemoved': 'Warga itu sudah dihapus.',
  // units (units/list.tsx)
  'units.title': 'Unit',
  'units.subtitleShort': 'Semua rumah dan unit di cluster. Klik unit untuk melihat detail dan warganya.',
  'units.filterAll': 'Semua',
  'units.filterUnregistered': 'Belum terdaftar',
  'units.search': 'Cari unit, mis. A-12',
  'units.searchWithResidents': 'Cari unit atau nama warga',
  'units.blockAll': 'Semua blok',
  'units.blockStats': '{{units}} unit · {{occupied}} dihuni · {{residents}} warga',
  'units.viewLabel': 'Tampilan',
  'units.viewMap': 'Peta',
  'units.viewTable': 'Tabel',
  'units.noMatch': 'Tidak ada unit yang cocok dengan pencarian atau filter ini.',
  'units.legendRegistered': 'Dihuni',
  'units.legendUnregistered': 'Belum terdaftar',
  'units.unregisteredHelp': 'Rumah dihuni, tapi belum ada yang punya akun di aplikasi. Ditandai dengan warna pucat.',
  'units.drawerResidents': 'Warga',
  'units.drawerBilling': 'Tagihan',
  'units.billThisMonth': 'Bulan ini',
  'units.billPaid': 'Lunas',
  'units.billUnpaid': 'Belum bayar',
  'units.billNone': 'Belum ditagih',
  'units.arrears': 'Tunggakan',
  'units.arrearsValue': '{{count}} bulan · Rp {{amount}}',
  'units.noArrears': 'Tidak ada',
  'units.arrearsSince': 'Belum bayar sejak {{month}}',
  'units.editUnit': 'Ubah unit',
  'units.more': 'Tindakan lain',
  'units.subtitle': 'Setiap rumah/unit di cluster. Tambahkan unit di sini, lalu buat kode undangan per unit agar warga dapat menautkan akun.',
  'units.newUnit': 'Unit baru',
  'units.empty': 'Belum ada unit. Tambahkan yang pertama untuk mulai orientasi warga.',
  'units.colBlock': 'Blok / Jalan',
  'units.colNumber': 'Nomor',
  'units.colLabel': 'Label',
  'units.colType': 'Tipe',
  'units.colOccupancy': 'Hunian',
  'units.deleteTitle': 'Hapus unit {{label}}?',
  'units.deleteWarning': 'Ini menghapus unit secara permanen. Unit yang masih memiliki warga, tagihan, atau catatan lain tidak dapat dihapus.',
  'units.deletedInfo': 'Unit {{label}} dihapus.',
  'units.alreadyRemoved': 'Unit itu sudah dihapus.',
  'units.editTitle': 'Ubah unit',
  'units.createTitle': 'Unit baru',
  'units.block': 'Blok / Jalan',
  'units.blockPlaceholder': 'A2 atau Matahari',
  'units.number': 'Nomor',
  'units.labelOptional': 'Label (opsional)',
  'units.labelPlaceholder': 'Kosongkan untuk memakai blok dan nomor',
  'units.type': 'Tipe',
  'units.occupancy': 'Hunian',
  'units.blockCannotEdit': 'Blok/jalan + nomor adalah identitas unit dan tidak dapat diubah — hapus dan buat ulang jika salah.',
  'units.occupiedChip': 'Dihuni',
  'units.vacantChip': 'Kosong',
  'units.ownerAbsentChip': 'Disewakan',
  'units.occHelpOccupied': 'Pemilik atau penghuni tetap tinggal di unit ini. Ini kasus normal.',
  'units.occHelpVacant': 'Tidak ada penghuni sama sekali. Status ini hanya catatan dan tidak menghentikan tagihan; pakai override biaya 0 untuk membebaskan.',
  'units.occHelpOwnerAbsent': 'Dihuni pihak lain (penyewa atau keluarga), sementara pemilik tinggal di tempat lain.',
  'units.typeHouse': 'Rumah',
  'units.typeApartment': 'Apartemen',
  'units.typeRuko': 'Ruko',
  'units.colUnit': 'Unit',
  'units.tabSingle': 'Satu per satu',
  'units.tabBulk': 'Impor massal',
  'import.step1': '1. Unduh template',
  'units.import.step1Help': 'Isi satu unit per baris di Excel atau Google Sheets. Contoh di dalamnya boleh dihapus.',
  'import.template': 'Unduh template',
  'import.step2': '2. Unggah file yang sudah diisi',
  'import.step2Help': 'Simpan tetap sebagai CSV. Kami periksa setiap baris sebelum ada yang ditambahkan.',
  'import.choose': 'Pilih file CSV',
  'import.fileEmpty': 'File ini kosong. Isi minimal satu baris di bawah judul kolom.',
  'units.import.fileNoNumber': 'Kolom "Nomor" tidak ditemukan. Pakai template agar judul kolomnya sesuai.',
  'import.summary': '{{ready}} siap diimpor, {{problems}} bermasalah',
  'import.problemsHelp': 'Baris bermasalah akan dilewati. Perbaiki di file lalu unggah lagi, atau lanjutkan dengan yang siap.',
  'import.colRow': 'Baris',
  'import.colStatus': 'Status',
  'import.ok': 'Siap',
  'import.otherFile': 'Pilih file lain',
  'units.import.run': 'Impor {{count}} unit',
  'import.progress': 'Memproses… {{done}} dari {{total}}',
  'units.import.added': '{{count}} unit ditambahkan.',
  'import.failedCount': '{{count}} gagal:',
  'import.rowFailed': 'Baris {{line}} ({{what}}):',
  'import.again': 'Impor file lain',
  'units.import.errNumber': 'Nomor kosong',
  'units.import.errBlock': 'Blok / jalan kosong',
  'units.import.errTooLong': 'Blok atau nomor lebih dari {{max}} karakter',
  'units.import.errLabel': 'Label lebih dari {{max}} karakter',
  'units.import.errType': 'Tipe "{{value}}" tidak dikenal (Rumah, Apartemen, Ruko)',
  'units.import.errOccupancy': 'Hunian "{{value}}" tidak dikenal (Dihuni, Kosong)',
  'units.import.errExists': 'Unit ini sudah ada',
  'units.import.errDuplicate': 'Sama dengan baris {{line}}',
  // account dialogs (shared — users/AccountDialogs.tsx)
  'acc.creating': 'Membuat…',
  'acc.create': 'Buat',
  'acc.resetPassword': 'Atur ulang kata sandi',
  'acc.removeFromCluster': 'Hapus dari cluster ini',
  'acc.removeTitle': 'Hapus {{name}} dari cluster ini?',
  'acc.removeWarning': 'Jika ini satu-satunya cluster mereka, akun akan dihapus.',
  'acc.removing': 'Menghapus…',
  'acc.remove': 'Hapus',
  'acc.deleteTitle': 'Hapus {{name}}?',
  'acc.deleteWarning': 'Ini menonaktifkan login segera dan menghapusnya dari daftar.',
  'acc.editTitle': 'Ubah {{name}}',
  'acc.statusActive': 'Aktif',
  'acc.statusSuspended': 'Ditangguhkan',
  // developer managers (users/developer-managers.tsx)
  'devmgr.title': 'Manajer developer',
  'devmgr.subtitle': 'Login manajer yang melihat semua cluster di satu developer. Kata sandi sementara ditampilkan sekali saat dibuat.',
  'devmgr.newBtn': 'Manajer developer baru',
  'devmgr.empty': 'Belum ada manajer developer.',
  'devmgr.colDeveloper': 'Developer',
  'devmgr.dialogTitle': 'Manajer developer baru',
  'devmgr.devHelper': 'Hanya developer yang sudah memiliki setidaknya satu cluster.',
  // cluster admins (users/cluster-admins.tsx)
  'cadmin.title': 'Admin cluster',
  'cadmin.subtitle': 'Pilih cluster, lalu kelola login admin-nya.',
  'cadmin.noClusters': 'Tidak ada cluster tersedia.',
  'cadmin.noClusterYet': 'Belum ada cluster.',
  'cadmin.adminsTitle': 'Admin · {{name}}',
  'cadmin.createBtn': 'Buat admin cluster',
  'cadmin.noAdmins': 'Belum ada admin untuk cluster ini.',
  'cadmin.manages': 'mengelola {{count}} cluster',
  'cadmin.dialogTitle': 'Admin cluster baru · {{cluster}}',
  'cadmin.adding': 'Menambahkan…',
  'cadmin.add': 'Tambah',
  'team.title': 'Tim pengelola',
  'team.developer': 'Developer',
  'team.cluster': 'Cluster',
  'team.allClusters': 'Semua cluster',
  'team.createClusterFirst': 'Buat developer dan cluster terlebih dahulu sebelum menambah tim pengelola.',
  'team.assignTitle': 'Undang warga atau tim pengelola',
  'team.assignClusters': 'Cluster yang dikelola',
  'team.assignCluster': 'Tetapkan ke cluster',
  'team.companyAll': 'Admin perusahaan mendapat akses ke semua {{count}} cluster developer ini, termasuk cluster baru.',
  'team.companyAdmin': 'Admin perusahaan',
  'team.clusterAdmin': 'Admin cluster',
  'team.platformAdmin': 'Admin platform',
  'team.staff': 'Staf',
  'team.security': 'Keamanan',
  'team.status': 'Status',
  'team.remove': 'Hapus',
  'team.noMembers': 'Belum ada tim pengelola.',
  'team.assignedNotice': '{{email}} ditetapkan ke {{count}} cluster.',
  'team.invitedNotice': 'Undangan Clerk dikirim ke {{email}}.',
  'team.removedDeveloperNotice': '{{name}} dihapus dari developer ini.',
  'team.removedClusterNotice': '{{name}} dihapus dari cluster ini.',
  'team.fullName': 'Nama lengkap',
  'team.email': 'Alamat email',
  'team.role': 'Peran',
  'team.admin': 'Admin cluster — akses penuh ke cluster yang ditetapkan',
  'team.internal': 'Tim internal — pilih akses halaman',
  'team.resident': 'Warga — akses aplikasi warga',
  'team.pageAccess': 'Akses halaman',
  'team.unit': 'Unit',
  'team.block': 'Blok / Jalan',
  'team.unitNumber': 'Nomor unit',
  'team.send': 'Kirim undangan',
  'team.sending': 'Mengirim undangan…',
  'team.success': 'Undangan Clerk dikirim ke {{email}} sebagai {{role}}.',
  'team.perm.staff': 'Kelola tim',
  'team.perm.units': 'Kelola unit',
  'team.perm.residents': 'Kelola warga',
  'team.perm.invites': 'Kelola kode undangan warga',
  'team.perm.announcements': 'Buat pengumuman',
  'team.perm.facilities': 'Kelola fasilitas',
  'team.perm.bookings': 'Setujui pemesanan',
  'team.perm.payments': 'Kelola pembayaran dan laporan',
  'team.perm.complaints': 'Kelola keluhan',
  'team.perm.emergency': 'Lihat operasi darurat',
  // invite codes (users/residents.tsx)
  'invite.title': 'Warga',
  'invite.subtitle': 'Pilih cluster, lalu buat atau cabut kode undangan warga (satu kode aktif per unit).',
  'invite.codesTitle': 'Kode undangan · {{name}}',
  'invite.createBtn': 'Buat kode',
  'invite.empty': 'Belum ada kode undangan untuk cluster ini.',
  'invite.colCode': 'Kode',
  'invite.colUses': 'Penggunaan',
  'invite.revoke': 'Cabut',
  'invite.createdTitle': 'Kode undangan dibuat',
  'invite.shareNote': 'Bagikan kepada warga — kedaluwarsa dalam 24 jam.',
  'invite.dialogTitle': 'Kode undangan baru · {{cluster}}',
  'invite.maxUses': 'Maksimal penggunaan',
  'invite.maxUsesHelper': 'Berapa akun yang dapat mendaftar dengan kode ini (default 2).',
  'invite.noUnits': 'Setiap unit sudah memiliki kode aktif, atau cluster ini belum memiliki unit. Tambahkan unit terlebih dahulu.',
  // security (users/security.tsx)
  'sec.title': 'Keamanan',
  'sec.subtitle': 'Pilih cluster, lalu kelola login petugas keamanan-nya.',
  'sec.sectionTitle': 'Keamanan · {{name}}',
  'sec.createBtn': 'Buat akun keamanan',
  'sec.empty': 'Belum ada akun keamanan untuk cluster ini.',
  'sec.dialogTitle': 'Akun keamanan baru · {{cluster}}',
  // bookings (bookings/list.tsx + status-chip.tsx)
  'book.title': 'Antrean pemesanan',
  'book.subtitle': 'Setujui atau tolak permintaan warga. Fasilitas konfirmasi otomatis tidak muncul di Tertunda.',
  'book.emptyAll': 'Belum ada pemesanan.',
  'book.emptyStatus': 'Tidak ada pemesanan {{status}}.',
  'book.colFacility': 'Fasilitas',
  'book.colWhen': 'Waktu',
  'book.colUnit': 'Unit',
  'book.approve': 'Setujui',
  'book.reject': 'Tolak',
  'book.approvedMsg': 'Pemesanan pada {{facility}} disetujui.',
  'book.rejectTitle': 'Tolak pemesanan',
  'book.rejectInfo': 'Warga akan melihat alasan ini. Bersifat spesifik agar dapat menjadwalkan ulang.',
  'book.reason': 'Alasan',
  'book.reasonPlaceholder': 'mis. Fasilitas dalam perawatan hari itu',
  'book.reasonRequired': 'Alasan wajib diisi.',
  'book.rejecting': 'Menolak…',
  'book.rejectBtn': 'Tolak pemesanan',
  'book.rejectedMsg': 'Pemesanan pada {{facility}} ditolak.',
  'book.statusPending': 'Tertunda',
  'book.statusConfirmed': 'Dikonfirmasi',
  'book.statusCancelled': 'Dibatalkan',
  'book.statusRejected': 'Ditolak',
  'book.statusCompleted': 'Selesai',
  // complaints (complaints/chips.tsx + list.tsx + show.tsx)
  'comp.title': 'Keluhan',
  'comp.subtitle': 'Laporan warga — publik dan privat. Gerakkan melalui Sedang diproses dan Selesai (resolusi memerlukan foto bukti).',
  'comp.filterCategory': 'Kategori',
  'comp.filterVisibility': 'Visibilitas',
  'comp.filterBlock': 'Blok / Jalan',
  'comp.thisMonth': 'Bulan ini',
  'comp.sortLabel': 'Urutkan',
  'comp.sortNewest': 'Terbaru dulu',
  'comp.sortOldest': 'Terlama dulu',
  'comp.empty': 'Tidak ada keluhan yang cocok dengan filter tersebut.',
  'comp.colTitle': 'Judul',
  'comp.colBlockUnit': 'Unit',
  'comp.colVisibility': 'Visibilitas',
  'comp.colObserved': 'Diamati',
  'comp.colFiled': 'Dilaporkan',
  'comp.statusNew': 'Baru',
  'comp.statusInProgress': 'Sedang diproses',
  'comp.statusResolved': 'Selesai',
  'comp.statusCancelled': 'Dibatalkan',
  'comp.catFacilityDamage': 'Kerusakan fasilitas',
  'comp.catCleanliness': 'Kebersihan',
  'comp.catNoise': 'Kebisingan',
  'comp.catLighting': 'Pencahayaan',
  'comp.catSecurity': 'Keamanan',
  'comp.catOther': 'Lainnya',
  'comp.backToList': 'Kembali ke keluhan',
  'comp.notFound': 'Keluhan tidak ditemukan.',
  'comp.moveToInProgress': 'Pindah ke Sedang diproses',
  'comp.markResolved': 'Tandai selesai',
  'comp.visibilityLine': 'Visibilitas: {{vis}} · Diamati {{observed}} · Dilaporkan {{filed}}',
  'comp.detailsSection': 'Detail',
  'comp.location': 'Lokasi',
  'comp.reportedBy': 'Dilaporkan oleh',
  'comp.inProgressAt': 'Mulai diproses',
  'comp.resolvedAt': 'Diselesaikan pada',
  'comp.cancelledAt': 'Dibatalkan pada',
  'comp.resolutionNotes': 'Catatan resolusi',
  'comp.residentPhoto': 'Foto warga',
  'comp.noResidentPhoto': 'Tidak ada foto yang diunggah oleh warga.',
  'comp.resolutionPhoto': 'Foto resolusi',
  'comp.notResolved': 'Belum diselesaikan.',
  'comp.resolveTitle': 'Tandai selesai',
  'comp.r2Warning': 'Penyimpanan R2 belum dikonfigurasi — foto disimpan secara inline. Di produksi, siapkan binding FILES untuk beralih ke penyimpanan R2 nyata.',
  'comp.resolveInfo': 'Unggah foto sebagai bukti. Warga akan melihat ini di halaman detail mereka.',
  'comp.pickPhoto': 'Pilih foto',
  'comp.selectedPhoto': 'Dipilih: {{name}}',
  'comp.notesOptional': 'Catatan (opsional)',
  'comp.notesPlaceholder': 'mis. mengganti lampu rusak, menguji kedua sakelar',
  'comp.resolving': 'Menyelesaikan…',
  'comp.markedInProgress': 'Ditandai sedang diproses. Warga diberi tahu.',
  'comp.markedResolved': 'Ditandai selesai. Warga diberi tahu.',
  'comp.noPhoto': 'Pilih foto resolusi untuk diunggah.',
  // facilities (facilities/*.tsx)
  'fac.title': 'Fasilitas',
  'fac.subtitle': 'Fasilitas yang dapat dipesan di cluster Anda. Alihkan persetujuan untuk mengontrol konfirmasi otomatis vs antrean.',
  'fac.newFacility': 'Fasilitas baru',
  'fac.empty': 'Belum ada fasilitas.',
  'fac.addFirst': 'Tambah yang pertama',
  'fac.colHours': 'Jam',
  'fac.colSlot': 'Slot',
  'fac.colApproval': 'Persetujuan',
  'fac.hours24h': '24 jam',
  'fac.approvalRequired': 'Diperlukan',
  'fac.approvalAuto': 'Konfirmasi otomatis',
  'fac.walkIn': 'Walk-in',
  'fac.statusActive': 'Aktif',
  'fac.statusHidden': 'Tersembunyi',
  'fac.backToList': 'Kembali ke fasilitas',
  'fac.createTitle': 'Fasilitas baru',
  'fac.createSubtitle': 'Tentukan fasilitas yang dapat dipesan warga. Panjang slot mengontrol seberapa granular pilihan pemesanan.',
  'fac.editTitle': 'Ubah fasilitas',
  'fac.notFound': 'Fasilitas tidak ditemukan. Mungkin sudah dihapus; kembali ke daftar lalu coba lagi.',
  'fac.saveChanges': 'Simpan perubahan',
  'fac.fieldName': 'Nama',
  'fac.nameHelper': 'mis. Lapangan Padel 1',
  'fac.fieldType': 'Tipe',
  'fac.fieldDesc': 'Deskripsi (opsional)',
  'fac.fieldCapacity': 'Kapasitas (opsional)',
  'fac.capacityHelper': 'Orang per slot',
  'fac.fieldOpenTime': 'Jam buka',
  'fac.timeHelper': 'Biarkan kosong untuk 24 jam',
  'fac.fieldCloseTime': 'Jam tutup',
  'fac.fieldSlot': 'Panjang slot',
  'fac.fieldImageUrl': 'URL gambar (opsional)',
  'fac.imageHelper': 'Ditampilkan sebagai sampul fasilitas di mobile. Maks 3 MB (JPG/PNG/WebP).',
  'fac.fieldImage': 'Gambar fasilitas (opsional)',
  'fac.imageUpload': 'Unggah gambar',
  'fac.imageReplace': 'Ganti gambar',
  'fac.imageRemove': 'Hapus',
  'fac.imageBadType': 'Format gambar harus JPG, PNG, atau WebP.',
  'fac.imageTooBig': 'Ukuran gambar maksimal 3 MB.',
  'fac.requiresBooking': 'Perlu pemesanan',
  'fac.bookingHelper': 'Jika dinonaktifkan, fasilitas bersifat walk-in: warga langsung datang, tanpa slot atau pemesanan (mis. kolam renang).',
  'fac.requiresApproval': 'Pemesanan memerlukan persetujuan staf',
  'fac.approvalHelper': 'Jika dinonaktifkan, pemesanan dikonfirmasi otomatis. Jika diaktifkan, masuk ke antrean persetujuan.',
  'fac.isActive': 'Aktif (warga dapat memesan fasilitas ini)',
  'fac.validName': 'Nama wajib diisi.',
  'fac.validSlot': 'Panjang slot minimal 5 menit.',
  'fac.validTimes': 'Atur jam buka dan tutup, atau biarkan keduanya kosong.',
  'fac.validCapacity': 'Kapasitas harus bilangan positif.',
  'fac.submitCreate': 'Buat fasilitas',
  'fac.saveFailed': 'Gagal menyimpan.',
  'fac.typePadel': 'Padel',
  'fac.typeTennis': 'Tenis',
  'fac.typeBasketball': 'Basket',
  'fac.typeGym': 'Gym',
  'fac.typePool': 'Kolam renang',
  'fac.typeMeeting': 'Ruang rapat',
  'fac.typeBbq': 'Area BBQ',
  'fac.typeOther': 'Lainnya',
  // platform — dashboard (platform/dashboard.tsx)
  'plt.overviewTitle': 'Gambaran platform',
  'plt.overviewSubtitle': 'Ringkasan perusahaan klien dan cluster propertinya.',
  'plt.tileDevelopers': 'Developer',
  'plt.tileArchived': '{{count}} diarsipkan',
  'plt.tileClusters': 'Cluster',
  'plt.tileUnits': 'Unit',
  'plt.tileUnitsInActive': 'di cluster aktif',
  'plt.tileOccupied': 'Unit terisi',
  'plt.tileOccupiedPct': '{{pct}}% · semua cluster',
  'plt.tileResidents': 'Warga',
  'plt.tileResidentsSub': 'akun terhubung',
  'plt.tileAdmins': 'Akun admin',
  'plt.tileAdminsSub': 'admin perusahaan + cluster',
  'plt.tileSecurity': 'Keamanan',
  'plt.tileSecuritySub': 'login penjaga',
  'plt.tileInviteCodes': 'Kode undangan aktif',
  'plt.tileInviteCodesSub': 'menunggu orientasi',
  'plt.occupancyTitle': 'Hunian',
  'plt.allDevelopers': 'Semua developer',
  'plt.allClusters': 'Semua cluster',
  'plt.occupied': 'terisi',
  'plt.scopeAll': 'total semua cluster',
  'plt.scopeDev': 'total developer',
  'plt.scopeCluster': 'cluster terpilih',
  'plt.chartTitle': 'Unit terisi / total per developer',
  'plt.noDevs': 'Belum ada developer.',
  // platform — developers (platform/developers.tsx)
  'plt.devTitle': 'Developer',
  'plt.devSubtitle': 'Setiap developer klien dapat memiliki banyak cluster properti. Undang admin developer melalui Warga › Tim pengelola.',
  'plt.newDev': 'Developer baru',
  'plt.noDevYet': 'Belum ada developer klien. Buat yang pertama.',
  'plt.noClusters': 'Tidak ada cluster.',
  'plt.archiveDevTitle': 'Arsipkan {{name}}?',
  'plt.archiveDevWarning': 'Perusahaan dan semua cluster-nya akan offline sekarang dan dihapus permanen dalam 30 hari. Anda dapat memulihkannya dari halaman Arsip hingga saat itu.',
  'plt.archiving': 'Mengarsipkan…',
  'plt.archive': 'Arsipkan',
  'plt.newDevTitle': 'Developer klien baru',
  'plt.newDevNote': 'Buat developer klien, lalu tambahkan cluster dan undang adminnya.',
  'plt.devNameField': 'Nama perusahaan developer',
  'plt.legacyName': 'Properti lama tanpa perusahaan',
  'plt.legacyBadge': 'Perlu penetapan',
  'plt.legacyHelp': 'Data lama dari migrasi awal. Tetapkan perusahaan pemiliknya melalui Cluster → Ubah → Lanjutan. Properti:',
  'plt.legacyHelpInClusters': 'Data properti lama. Gunakan Ubah → Lanjutan untuk menetapkan perusahaan klien yang benar.',
  // platform — clusters (platform/clusters.tsx)
  'plt.clusterTitle': 'Cluster',
  'plt.clusterSubtitle': 'Cluster properti dikelompokkan menurut perusahaan klien.',
  'plt.newCluster': 'Cluster baru',
  'plt.noDevFirst': 'Buat perusahaan klien terlebih dahulu, lalu tambahkan cluster propertinya.',
  'plt.noClusterYet': 'Belum ada cluster.',
  'plt.colCluster': 'Cluster',
  'plt.colType': 'Tipe',
  'plt.colLocation': 'Lokasi',
  'plt.colSlug': 'Slug',
  'plt.tooltipEditContact': 'Ubah info kontak',
  'plt.archiveClusterTitle': 'Arsipkan {{name}}?',
  'plt.archiveClusterWarning': 'Cluster ini akan offline sekarang dan dihapus permanen dalam 30 hari — pulihkan dari halaman Arsip hingga saat itu.',
  'plt.newClusterTitle': 'Cluster baru',
  'plt.devField': 'Perusahaan klien',
  'plt.clusterDetailsOpt': 'Detail cluster (opsional)',
  'plt.fieldAddress': 'Alamat',
  'plt.fieldCity': 'Kota',
  'plt.fieldProvince': 'Provinsi',
  'plt.fieldPostalCode': 'Kode pos',
  'plt.fieldPhone': 'Telepon kontak',
  'plt.fieldContactEmail': 'Email kontak',
  'plt.fieldClusterName': 'Nama cluster',
  'plt.fieldPropertyType': 'Tipe properti',
  'plt.propertyTypeHousing': 'Rumah tapak',
  'plt.propertyTypeApartment': 'Apartemen',
  'plt.editClusterTitle': 'Ubah {{name}}',
  'plt.contactSection': 'Kontak / catatan detail',
  'plt.moreBtn': 'Lainnya',
  'plt.advancedSection': 'Lanjutan — nama & perusahaan',
  'plt.devMoveHelper': 'Memindahkan cluster ke perusahaan lain dan memperbarui akses admin perusahaan.',
  'plt.clusterCount': '{{count}} cluster',
  // platform — archived (platform/archived.tsx)
  'plt.archivedTitle': 'Arsip',
  'plt.archivedSubtitle': 'Perusahaan dan cluster yang diarsipkan offline dan akan dihapus permanen setelah {{days}} hari. Pulihkan untuk mengembalikannya.',
  'plt.archiveDevSection': 'Perusahaan',
  'plt.archiveClusterSection': 'Cluster',
  'plt.archiveNone': 'Tidak ada yang diarsipkan.',
  'plt.colDeletesIn': 'Dihapus dalam',
  'plt.colAction': 'Tindakan',
  'plt.restore': 'Pulihkan',
  'plt.daysLeft': '{{days}} hari',
  // platform — CredentialsDialog (platform/CredentialsDialog.tsx)
  'plt.credTitle': 'Akun dibuat',
  'plt.credNote': 'Serahkan sekarang — kata sandi hanya ditampilkan sekali.',
  'plt.credPassword': 'Kata sandi sementara',
  // reminders (reminders/list.tsx)
  'rem.title': 'Pengingat pembayaran',
  'rem.subtitle': 'Kirim pengingat pembayaran kepada warga di cluster ini.',
  'rem.tileUnitsOwe': 'Unit yang menunggak',
  'rem.tileTotalOutstanding': 'Total tunggakan',
  'rem.tileLastSent': 'Pengingat terakhir dikirim',
  'rem.recipientsLabel': 'Penerima',
  'rem.scopeOwing': 'Unit yang menunggak',
  'rem.scopeAll': 'Semua unit',
  'rem.messageField': 'Pesan',
  'rem.messageHelper': 'Setiap warga juga melihat baris mereka sendiri, mis. "Unit B-12: tunggakan Rp 350.000, jatuh tempo 25 Jun 2026."',
  'rem.colOutstanding': 'Tunggakan',
  'rem.colDue': 'Jatuh tempo',
  'rem.noMainUser': 'tidak ada Pengguna Utama — dilewati',
  'rem.sendBtn': 'Kirim pengingat ke {{count}} warga{{plural}}',
  'rem.emptyOwing': 'Tidak ada unit yang menunggak — tidak ada yang perlu dikirim.',
  'rem.emptyAll': 'Belum ada unit di cluster ini.',
  'rem.confirmTitle': 'Kirim pengingat pembayaran?',
  'rem.confirmText': 'Ini akan memberi tahu {{count}} warga{{plural}} (Pengguna Utama dari setiap {{scope}}).',
  'rem.scopeOwingLabel': 'unit yang menunggak',
  'rem.scopeAllLabel': 'unit',
  'rem.sending': 'Mengirim…',
  'rem.send': 'Kirim',
  // operations (operations/list.tsx)
  'ops.title': 'Operasi',
  'ops.subtitle': 'Log hanya-baca aktivitas gerbang di cluster — check-in pengunjung, paket di pos keamanan, dan peringatan darurat.',
  'ops.tabVisitors': 'Check-in pengunjung',
  'ops.tabPackages': 'Paket',
  'ops.tabEmergency': 'Peringatan darurat',
  'ops.emptyVisitors': 'Belum ada check-in pengunjung.',
  'ops.emptyPackages': 'Belum ada paket tercatat.',
  'ops.emptyEmergency': 'Tidak ada peringatan darurat — kabar baik.',
  'ops.colWhen': 'Waktu',
  'ops.colVisitor': 'Pengunjung',
  'ops.colHost': 'Tuan rumah',
  'ops.colScannedBy': 'Dipindai oleh',
  'ops.colRecipient': 'Penerima',
  'ops.colCarrier': 'Pengirim',
  'ops.colKind': 'Jenis',
  'ops.colRaisedBy': 'Dilaporkan oleh',
  'ops.pkgPickedUp': 'Diambil',
  'ops.pkgCancelled': 'Dibatalkan',
  'ops.pkgWaiting': 'Menunggu',
  'ops.alertResolved': 'Selesai',
  'ops.alertCancelled': 'Dibatalkan',
  'ops.alertActive': 'Aktif',
  // branding (resident-app/branding.tsx)
  'brand.title': 'Aplikasi warga — Branding',
  'brand.subtitle': 'Foto sampul di bagian atas layar beranda aplikasi warga untuk {{name}}. Gunakan gambar lanskap lebar (sekitar 16:10) — dipotong untuk mengisi.',
  'brand.tooBig': 'Gambar terlalu besar — pilih file di bawah 5 MB.',
  'brand.updated': 'Gambar hero diperbarui',
  'brand.reverted': 'Dikembalikan ke gambar default',
  'brand.useDefault': 'Menggunakan gambar default aplikasi',
  'brand.working': 'Memproses…',
  'brand.upload': 'Unggah gambar',
  'brand.remove': 'Hapus (gunakan default)',
  // change password dialog (components/ChangePasswordDialog.tsx)
  'pwd.title': 'Ubah kata sandi',
  'pwd.changed': 'Kata sandi Anda telah diubah.',
  'pwd.current': 'Kata sandi saat ini',
  'pwd.new': 'Kata sandi baru',
  'pwd.tooShort': 'Minimal {{min}} karakter',
  'pwd.confirm': 'Konfirmasi kata sandi baru',
  'pwd.mismatch': 'Kata sandi tidak cocok',
  'pwd.changing': 'Menyimpan…',
  'pwd.changeBtn': 'Ubah kata sandi',
  // edit profile dialog (components/EditProfileDialog.tsx)
  'prof.title': 'Ubah profil',
  'prof.nameLabel': 'Nama tampilan',
  'prof.nameRequired': 'Nama tidak boleh kosong',
  'prof.upload': 'Unggah foto',
  'prof.replace': 'Ganti foto',
  'prof.uploading': 'Mengunggah…',
  'prof.remove': 'Hapus foto',
  'prof.tooBig': 'Ukuran gambar maksimal {{max}} MB',
  'prof.photoHint': 'JPG, PNG, atau WebP. Maksimal {{max}} MB.',
  'prof.save': 'Simpan',
  'prof.saving': 'Menyimpan…',
  // cluster switcher (components/ClusterSwitcher.tsx)
  'cluster.fallback': 'Cluster',
  'cluster.switchAriaLabel': 'ganti cluster',
  // notification bell (components/NotificationBell.tsx)
  'notif.title': 'Notifikasi',
  'notif.empty': 'Tidak ada yang perlu diperhatikan saat ini.',
  'notif.emergencies': 'Peringatan darurat aktif',
  'notif.bookingsPending': 'Pemesanan untuk disetujui',
  'notif.complaintsNew': 'Keluhan baru',
  'notif.packagesNew': 'Paket baru',
  'notif.scansNew': 'Check-in pengunjung baru',
  // announcements (announcements/*.tsx)
  'ann.title': 'Pengumuman',
  'ann.subtitle': 'Pemadaman, pemeliharaan, catatan keamanan untuk warga Anda.',
  'ann.newBtn': 'Pengumuman baru',
  'ann.empty': 'Belum ada pengumuman.',
  'ann.composeFirst': 'Buat yang pertama',
  'ann.colTitle': 'Judul',
  'ann.colCategory': 'Kategori',
  'ann.colStatus': 'Status',
  'ann.colPublished': 'Diterbitkan',
  'ann.requiresAck': 'Perlu konfirmasi',
  'ann.statusPublished': 'Diterbitkan',
  'ann.statusDraft': 'Draf',
  'ann.publishedMsg': 'Diterbitkan. {{count}} warga diberi tahu.',
  'ann.deleteConfirm': 'Hapus "{{title}}"? Ini tidak dapat dibatalkan.',
  'ann.fieldCategory': 'Kategori',
  'ann.categoryRequired': 'Pilih kategori',
  'ann.categoryHelper': 'Menentukan warna pratinjau notifikasi push di aplikasi warga.',
  'ann.fieldTitle': 'Judul',
  'ann.titleRequired': 'Judul wajib diisi',
  'ann.titleMaxLen': 'Maks 200 karakter',
  'ann.titleHelper': 'Ditampilkan di push dan sebagai baris pertama di kotak masuk warga.',
  'ann.fieldBody': 'Isi (mendukung Markdown)',
  'ann.bodyRequired': 'Isi wajib diisi',
  'ann.bodyHelper': 'Tips: buat ~140 karakter pertama informatif — itu adalah pratinjau push.',
  'ann.pinToTop': 'Sematkan di atas daftar',
  'ann.requireAck': 'Minta warga untuk mengonfirmasi',
  'ann.publishNow': 'Terbitkan + beri tahu warga sekarang',
  'ann.saveDraft': 'Hapus centang untuk menyimpan sebagai draf yang dapat diterbitkan nanti.',
  'ann.saveFailed': 'Gagal menyimpan',
  'ann.saveChanges': 'Simpan perubahan',
  'ann.createBtn': 'Buat',
  'ann.publishedNotif': 'Diterbitkan! {{count}} warga diberi tahu.',
  'ann.savedDraft': 'Disimpan sebagai draf.',
  'ann.saved': 'Disimpan.',
  'ann.catOutage': 'Pemadaman',
  'ann.catMaintenance': 'Pemeliharaan',
  'ann.catGeneral': 'Umum',
  'ann.catSecurity': 'Keamanan',
  // common additions (shared across cluster-picker pages)
  'common.close': 'Tutup',
  // billing fees (billing/fees.tsx)
  'fee.title': 'Iuran',
  'fee.subtitle': 'Atur iuran bulanan yang dibayar setiap unit. Cron tagihan menerbitkannya setiap bulan sejak bulan mulai. Tambahkan penyesuaian per unit untuk unit yang membayar berbeda atau dibebaskan.',
  'fee.saved': 'Iuran bulanan disimpan.',
  'fee.monthlyFee': 'Iuran bulanan',
  'fee.amount': 'Jumlah',
  'fee.amountHelper': 'Yang dibayar setiap unit per bulan (sebelum penyesuaian).',
  'fee.effectiveFrom': 'Berlaku mulai',
  'fee.effectiveFromHelper': 'Bulan pertama ditagih.',
  'fee.dueDay': 'Tanggal jatuh tempo',
  'fee.dueDayHelper': 'Tanggal dalam bulan (1–28).',
  'fee.saveFee': 'Simpan iuran',
  'fee.setupFee': 'Atur iuran bulanan',
  'fee.overridesTitle': 'Penyesuaian per unit',
  'fee.addOverride': 'Tambah penyesuaian',
  'fee.setFeeFirst': 'Atur iuran bulanan dulu, lalu tambahkan penyesuaian.',
  'fee.noOverrides': 'Tidak ada penyesuaian — setiap unit membayar iuran bulanan.',
  'fee.colUnit': 'Unit',
  'fee.colPays': 'Membayar',
  'fee.exempt': 'Bebas',
  'fee.removeTooltip': 'Hapus (kembali ke iuran bulanan)',
  'fee.editOverride': 'Ubah penyesuaian',
  'fee.exemptCheckbox': 'Bebas (tidak membayar)',
  'fee.overrideAmountHelper': 'Yang dibayar unit ini per bulan menggantikan iuran cluster.',
  // segment input (components/SegmentInput.tsx)
  'seg.typeAlpha': 'Huruf',
  'seg.typeNum': 'Angka',
  'seg.typeCustom': 'Bebas',
  'seg.customPlaceholder': 'teks bebas',
  'seg.removeSegment': 'Hapus segmen',
  'seg.addSegment': 'Tambah segmen',
  // platform — cluster address scheme (platform/clusters.tsx)
  'plt.addrSchemeTitle': 'Skema alamat unit',
  'plt.addrSchemeHelper': 'Nomor selalu wajib. Aktifkan bagian lain yang digunakan cluster ini.',
  'plt.addrStreet': 'Nomor jalan',
  'plt.addrBlock': 'Blok',
  // units — residents dialog (units/list.tsx)
  'units.subtitleResidentsHint': 'Klik unit untuk melihat warganya dan menetapkan utama.',
  'units.residentsTitle': 'Warga · {{label}}',
  'units.residentsHelper': 'Warga utama menerima email tagihan. Gunakan bintang untuk menetapkan utama yang berbeda.',
  'units.noResidentsLinked': 'Belum ada warga tertaut ke unit ini. Buat kode undangan agar warga dapat mendaftar.',
  'units.colPrimary': 'Utama',
  'units.primaryChip': 'Utama',
  'units.primaryTooltip': 'Warga utama',
  'units.makePrimary': 'Jadikan utama',
  // invite codes — new dialogs + segment flow (users/residents.tsx)
  'invite.clustersChip': '{{count}} cluster',
  'invite.colExpiresIn': 'Kedaluwarsa dalam',
  'invite.deleteCodeTooltip': 'Hapus kode ini secara permanen',
  'invite.deleteCodeTitle': 'Hapus kode {{code}}?',
  'invite.deleteCodeWarningUnit': 'Ini menghapus kode untuk {{unit}} secara permanen. Tidak dapat dibatalkan — buat kode baru jika warga masih perlu mendaftar.',
  'invite.deleteCodeWarning': 'Ini menghapus kode secara permanen. Tidak dapat dibatalkan — buat kode baru jika warga masih perlu mendaftar.',
  'invite.buildAddress': 'Susun alamat unit. Kode memungkinkan hingga 3 akun mendaftar dan kedaluwarsa dalam 24 jam.',
  'invite.segStreet': 'Nomor jalan',
  'invite.segBlock': 'Blok',
  'invite.segNumber': 'Nomor',
  'invite.unitPrefix': 'Unit',
  'invite.existingCodeTitle': 'Unit ini sudah punya kode',
  'invite.existingCodeBody': 'Bagikan kode yang ada kepada warga — masih berlaku. Atau ganti dengan yang baru (kode lama berhenti berfungsi).',
  'invite.codeUsed': 'digunakan {{used}}/{{max}}',
  'invite.codeExpires': 'kedaluwarsa dalam {{expires}}',
  'invite.replaceWithNew': 'Ganti dengan baru',
  'invite.replacing': 'Mengganti…',
  'invite.unitFullTitle': 'Unit sudah penuh',
  'invite.generateAnyway': 'Tetap buat',
  'invite.generating': 'Membuat…',
  // cleanup additions
  'ann.editTitle': 'Ubah pengumuman',
  'ann.publish': 'Terbitkan',
  'ann.coverImage': 'Gambar sampul (opsional)',
  'ann.coverUpload': 'Unggah gambar',
  'ann.coverRemove': 'Hapus gambar',
  'ann.coverWorking': 'Mengunggah…',
  'ann.coverTooBig': 'Gambar harus di bawah 2 MB.',
  'ann.coverBadType': 'Format tidak didukung. Gunakan JPG, PNG, atau WebP.',
  'comp.visPublic': 'publik',
  'comp.visPrivate': 'privat',
  // landing (public product-intro page)
  'landing.nav.features': 'Fitur',
  'landing.nav.management': 'Pengelola',
  'landing.nav.residents': 'Warga',
  'landing.nav.contact': 'Kontak',
  'landing.nav.login': 'Masuk Admin',
  'landing.hero.kicker': 'Manajemen hunian cluster',
  'landing.hero.title': 'Satu aplikasi untuk seluruh hunian cluster Anda',
  'landing.hero.sub':
    'Pengumuman, iuran IPL, fasilitas, keluhan, dan tamu, semua dalam satu tempat untuk pengelola dan warga.',
  'landing.hero.ctaLogin': 'Masuk Admin',
  'landing.hero.ctaApp': 'Unduh aplikasi warga',
  'landing.hero.imageAlt': 'Foto cluster hunian',
  'landing.features.title': 'Semua kebutuhan pengelolaan, dalam satu platform',
  'landing.features.announcements': 'Pengumuman',
  'landing.features.billing': 'Tagihan IPL dan pembayaran',
  'landing.features.facilities': 'Booking fasilitas',
  'landing.features.complaints': 'Keluhan warga',
  'landing.features.security': 'Tamu dan keamanan',
  'landing.features.reports': 'Laporan keuangan',
  'landing.mgmt.kicker': 'Untuk pengelola',
  'landing.mgmt.title': 'Dashboard web yang rapi untuk admin cluster',
  'landing.mgmt.b1': 'Kelola unit dan data warga',
  'landing.mgmt.b2': 'Buat tagihan IPL otomatis tiap bulan',
  'landing.mgmt.b3': 'Pantau pembayaran dan tunggakan',
  'landing.mgmt.b4': 'Atur staf dan keamanan',
  'landing.mgmt.b5': 'Lihat laporan keuangan',
  'landing.mgmt.cta': 'Masuk Admin',
  'landing.res.kicker': 'Untuk warga',
  'landing.res.title': 'Aplikasi mobile untuk penghuni',
  'landing.res.b1': 'Lihat dan bayar tagihan',
  'landing.res.b2': 'Booking fasilitas',
  'landing.res.b3': 'Lapor keluhan',
  'landing.res.b4': 'Terima pengumuman',
  'landing.res.b5': 'Undang tamu',
  'landing.res.comingSoon': 'Segera hadir',
  'landing.how.title': 'Mulai dalam 3 langkah',
  'landing.how.s1': 'Pengelola membuat cluster dan unit',
  'landing.how.s2': 'Warga bergabung dengan kode undangan',
  'landing.how.s3': 'Iuran dan layanan berjalan otomatis',
  'landing.contact.title': 'Tertarik memakai ResidenOne di cluster Anda?',
  'landing.contact.sub': 'Hubungi tim kami untuk demo.',
  'landing.contact.whatsapp': 'Hubungi via WhatsApp',
  'landing.contact.email': 'Email kami',
  'landing.footer.rights': '© 2026 ResidenOne · PT Semesta Digital Integrasi',
};

const EN: Dict = {
  'common.account': 'Account',
  'common.rowsPerPage': 'Rows per page',
  'common.displayedRows': '{{from}}–{{to}} of {{count}}',
  'common.editProfile': 'Edit profile',
  'common.manageAccount': 'Manage Clerk account',
  'common.changePassword': 'Change password',
  'common.logout': 'Log out',
  'common.openNav': 'Open navigation',
  'nav.dashboard': 'Dashboard',
  'nav.peopleProperty': 'People & property',
  'nav.units': 'Units',
  'nav.residents': 'Residents',
  'nav.billing': 'Billing',
  'nav.fees': 'Fees',
  'nav.invoices': 'Invoices',
  'nav.reports': 'Reports',
  'nav.amenities': 'Amenities',
  'nav.facilities': 'Facilities',
  'nav.bookings': 'Bookings',
  'nav.community': 'Community',
  'nav.announcements': 'Announcements',
  'nav.complaints': 'Complaints',
  'nav.paymentReminders': 'Payment reminders',
  'nav.securityOps': 'Security & ops',
  'nav.operations': 'Operations',
  'nav.platform': 'Platform',
  'nav.developers': 'Developers',
  'nav.clusters': 'Clusters',
  'nav.archived': 'Archived',
  'search.placeholder': 'Search the platform…',
  'search.minimum': 'Type at least 2 characters',
  'search.noResults': 'No results',
  'search.pages': 'Pages',
  'search.developers': 'Developers',
  'search.clusters': 'Clusters',
  'search.members': 'Management team',
  'nav.developerManagers': 'Developer managers',
  'nav.clusterAdmins': 'Cluster admins',
  'nav.security': 'Security',
  'nav.residentApp': 'Resident App',
  'nav.branding': 'Branding',
  'dash.greetingMorning': 'Good morning',
  'dash.greetingAfternoon': 'Good afternoon',
  'dash.greetingEvening': 'Good evening',
  'dash.health.rate': 'Collection rate',
  'dash.health.rateMonth': '{{month}} so far',
  'dash.health.arrears': 'Arrears',
  'dash.health.arrearsHouses': '{{count}} houses behind',
  'dash.health.noArrears': 'No arrears',
  'dash.health.seeReport': 'See the report',
  'dash.health.adoption': 'App adoption',
  'dash.health.adoptionMissing': '{{count}} houses not registered',
  'dash.health.adoptionAll': 'Every house is registered',
  'dash.health.bookings': 'Bookings waiting',
  'dash.health.bookingsWaiting': 'Need approval',
  'dash.health.handle': 'Handle',
  'dash.health.complaints': 'New complaints',
  'dash.health.complaintsWaiting': 'Not picked up yet',
  'dash.health.nothingWaiting': 'Nothing waiting',
  'dash.group.emergency': '{{count}} active emergencies',
  'dash.group.emergencyDetail': 'Check Operations now.',
  'dash.group.open': 'Open',
  'dash.group.booking': '{{facility}} booking',
  'dash.group.bookingWaiting': 'Waiting for approval',
  'dash.group.approve': 'Approve',
  'dash.group.details': 'Details',
  'dash.group.complaints': '{{count}} new complaints',
  'dash.group.complaintsDetail': 'Oldest: {{title}} · {{age}}',
  'dash.group.arrears': '{{count}} houses behind on fees',
  'dash.group.arrearsSerious': '{{serious}} of them 3+ months · Rp {{amount}} in total',
  'dash.group.arrearsAmount': 'Rp {{amount}} in total',
  'dash.group.remind': 'Send reminders',
  'dash.pace.title': 'This month\'s collection pace',
  'dash.pace.ahead': 'Day {{day}}: {{now}}% paid, ahead of {{month}} ({{then}}% by the same day).',
  'dash.pace.behind': 'Day {{day}}: {{now}}% paid, behind {{month}} ({{then}}% by the same day).',
  'dash.pace.noBills': 'No bills issued this month yet.',
  'dash.pace.dayN': 'Day {{day}}',
  'dash.pace.thisMonth': '{{month}} (this month)',
  'dash.pace.lastMonth': '{{month}} (last month)',
  'dash.feed.title': 'Recent activity',
  'dash.feed.empty': 'No activity in the last two weeks.',
  'dash.feed.payment': '{{unit}} paid Rp {{amount}}',
  'dash.feed.signup': '{{name}} joined · {{unit}}',
  'dash.feed.signupPending': '{{name}} signed up · waiting for an invite code',
  'dash.feed.complaint': 'New complaint: {{title}}',
  'dash.feed.booking': 'New booking: {{facility}}',
  'dash.ago.minutes': '{{count}} min ago',
  'dash.ago.hours': '{{count}} h ago',
  'dash.ago.yesterday': 'yesterday',
  'dash.permissionsCount': '{{count}} permissions',
  'dash.tileUnits': 'Units',
  'dash.tileResidents': 'Residents',
  'dash.tileOpenInvoices': 'Open invoices',
  'dash.tilePendingBookings': 'Pending bookings',
  'dash.tileOpenComplaints': 'Open complaints',
  'dash.tileActiveEmergencies': 'Active emergencies',
  'dash.collectionThisMonth': 'Collection this month',
  'dash.amountCollected': 'Amount collected',
  'dash.housesPaid': 'Houses paid',
  'dash.collectedOutstanding': '{{pct}}% collected · Rp {{outstanding}} outstanding',
  'dash.statsFailed': "Couldn't load the dashboard numbers. Check your connection and try again.",
  'dash.billingFailed': "Couldn't load this month's collection summary.",
  'dash.retry': 'Retry',
  'dash.tileUnavailable': 'Not available',
  'dash.noBillsThisMonth': 'No bills issued this month yet.',
  'dash.action.title': 'Needs action',
  'dash.action.subtitle': 'Most urgent first.',
  'dash.action.empty': 'Nothing is waiting. Nice work.',
  'dash.action.today': 'Today',
  'dash.action.daysAgo': '{{count}}d ago',
  'dash.action.emergencyDetail': 'House {{unit}} · handle in Operations',
  'dash.action.arrearsTitle': 'House {{unit}}',
  'dash.action.arrearsDetail': '{{count}} months behind · send a reminder',
  'dash.action.bookingDetail': 'Starts {{when}} · needs approval',
  'dash.action.complaintDetail': 'House {{unit}}',
  'dash.action.kind.MEDICAL': 'Medical emergency',
  'dash.action.kind.FIRE': 'Fire',
  'dash.action.kind.SECURITY': 'Security emergency',
  'dash.action.kind.OTHER': 'Emergency',
  'dash.action.type.EMERGENCY': 'Emergency',
  'dash.action.type.ARREARS': 'Arrears',
  'dash.action.type.COMPLAINT': 'Complaint',
  'dash.action.type.BOOKING': 'Booking',
  'dash.avgResolve': 'Resolved in {{time}} on average',
  'dash.hours': '{{count}} h',
  'dash.days': '{{count}} days',
  'dash.complaintsThisMonth': 'Complaints this month',
  'dash.noComplaintsThisMonth': 'No complaints filed this month yet.',
  'dash.complaintsDone': 'Done',
  'dash.complaintsOpen': 'Still open',
  'dash.complaintsBreakdown': '{{pct}}% done · {{waiting}} waiting · {{inProgress}} in progress',
  'dash.emergencyBanner': '{{count}} active emergency alert(s). Needs attention now.',
  'dash.emergencyOpen': 'Open Operations',
  'rpt.headline': '{{collected}} collected of {{billed}}',
  'rpt.kpiCollected': 'Collected',
  'rpt.kpiOfBilled': 'of {{billed}} billed',
  'rpt.kpiSparkHint': 'Collected per month',
  'rpt.kpiRate': 'Collection rate',
  'rpt.kpiTarget': 'Target {{pct}}%',
  'rpt.kpiHousesPaid': 'Houses fully paid',
  'rpt.kpiNotYetPaid': '{{count}} not yet paid',
  'rpt.kpiAllPaid': 'Everyone has paid',
  'rpt.kpiSeeUnits': 'See by unit',
  'rpt.kpiArrears': 'Arrears today',
  'rpt.kpiArrearsHouses': '{{count}} houses behind',
  'rpt.kpiNoArrears': 'No arrears',
  'rpt.kpiSeeArrears': 'See the list',
  'rpt.statusTip': '{{status}}: {{count}} invoices · Rp {{amount}}',
  'rpt.statusTotal': '{{count}} invoices',
  'rpt.statusNone': 'No invoices in this period.',
  'bill.seriesCollected': 'Collected',
  'bill.seriesOwed': 'Still owed',
  'bill.seriesRate': 'Collection rate',
  'bill.clickMonthHint': 'Click a month to open its report.',
  'rpt.stillOwed': 'Still owed: {{amount}}',
  'bill.target': 'Target {{pct}}%',
  'bill.story.onTarget': 'Collection is steady at {{avg}}% on average, every month above the {{target}}% target.',
  'bill.story.belowTarget': 'Collection averages {{avg}}%. {{below}} of {{months}} months fell below the {{target}}% target.',
  'bill.story.arrears': '{{houses}} houses still owe Rp {{amount}} in total.',
  'bill.story.noArrears': 'No arrears.',
  'bill.arrearsContext': "Rp {{amount}} is {{months}} months of the whole cluster's fees · {{houses}} houses ({{share}}% of all houses).",
  'bill.historyTitle': 'Billing, last 12 months',
  'bill.historySubtitle': 'Money collected and still owed per month, with the share collected.',
  'bill.trendTooltip': 'collected Rp {{collected}}, owed Rp {{outstanding}}',
  'bill.arrearsTitle': 'Arrears',
  'bill.arrearsCount': '{{count}} houses',
  'bill.sendReminders': 'Send reminders',
  'bill.aging1': '≤ 1 month',
  'bill.aging2': '2–3 months',
  'bill.aging3': '> 3 months',
  'bill.noArrears': 'No arrears. Every house is paid up.',
  'bill.monthsBehind': '{{count}} mo. behind',
  'bill.since': 'Since {{month}}',
  'role.STAFF': 'Staff',
  'role.SECURITY': 'Security',
  'role.RESIDENT': 'Resident',
  'rpt.title': 'Reports',
  'rpt.subtitle': 'IPL collection across the cluster.',
  'rpt.sendReminders': 'Send payment reminders',
  'rpt.tabSummary': 'Summary',
  'rpt.tabByUnit': 'By unit',
  'rpt.period': 'Period',
  'rpt.periodAll': 'All time',
  'rpt.periodCurrent': 'Current month',
  'rpt.periodSelect': 'Select month…',
  'rpt.periodYear': 'Select year…',
  'rpt.yearLabel': 'Year {{year}}',
  'rpt.downloadYear': 'Download {{year}} report',
  'rpt.downloadMonth': 'Download {{month}} report',
  'rpt.download': 'Download',
  'rpt.csvStatus': 'Status',
  'rpt.csvPaidOn': 'Paid on',
  'rpt.csvMonthTotal': 'Total',
  'rpt.csvHouse': 'House',
  'rpt.csvBilled': 'Total billed (Rp)',
  'rpt.csvPaid': 'Paid (Rp)',
  'rpt.csvOwed': 'Owed (Rp)',
  'rpt.csvTotal': 'Total collected (Rp)',
  'rpt.csvPaidCell': 'Paid',
  'rpt.csvUnpaidCell': 'Unpaid',
  'bill.yearTitle': 'Billing in {{year}}',
  'bill.dailyTitle': 'Daily payments, {{month}}',
  'bill.dailySummary': '{{count}} payments received, Rp {{amount}} in total.',
  'bill.dailyEmpty': 'No payments this month yet.',
  'bill.dailyTooltip': '{{count}} payments, Rp {{amount}}',
  'bill.dailyDue': 'Due {{day}}',
  'bill.dailyBeforeDue': 'Before the due date',
  'bill.dailyAfterDue': 'After the due date',
  'rpt.year': 'Year',
  'rpt.month': 'Month',
  'rpt.outstanding': 'Outstanding',
  'rpt.invoicesByStatus': 'Invoices by status, {{period}}',
  'rpt.statusDraft': 'Draft',
  'rpt.statusOpen': 'Unpaid',
  'rpt.statusPaid': 'Paid',
  'rpt.statusOverdue': 'Overdue',
  'rpt.statusVoid': 'Void',
  'rpt.last12Months': 'Last 12 months — {{unit}}',
  'rpt.legendPaid': 'Paid',
  'rpt.legendUnpaid': 'Unpaid',
  'rpt.legendNotBilled': 'Not billed',
  'rpt.tipNotBilled': '{{month}}: Not billed',
  'rpt.tipPaid': '{{month}}: Paid {{amount}}',
  'rpt.tipPaidOn': '{{month}}: Paid {{amount}} on {{date}}',
  'rpt.tipUnpaid': '{{month}}: Unpaid {{amount}}',
  'rpt.findUnit': 'Find a unit',
  'rpt.findUnitPlaceholder': 'e.g. A24, B12…',
  'rpt.byUnitHelp': 'Click a row to see the last 12 months of payments. Click the unit name for the full invoice history.',
  'rpt.noUnitsMatch': 'No units match.',
  'rpt.colUnit': 'Unit',
  'rpt.colThisMonth': 'This month',
  'rpt.colMonthsBehind': 'Months behind',
  'rpt.colOldestUnpaid': 'Oldest unpaid',
  'rpt.monthsBehindValue': '{{count}} mo',
  'rpt.upToDate': 'Up to date',
  'rpt.collapse': 'collapse',
  'rpt.expand': 'expand',
  'rpt.unitInvoicesTitle': 'Unit {{unit}} — invoices',
  'rpt.noUnitInvoices': 'No invoices for this unit yet.',
  'rpt.colMonth': 'Month',
  'rpt.colAmount': 'Amount',
  'rpt.colStatus': 'Status',
  'rpt.colPaidOn': 'Paid on',
  'inv.title': 'Invoices',
  'inv.subtitle': 'Resident IPL and other charges. Mark paid manually for transfers / cash.',
  'inv.status': 'Status',
  'inv.filterAll': 'All',
  'inv.newInvoice': 'New invoice',
  'inv.statusDraft': 'Draft',
  'inv.statusOpen': 'Open',
  'inv.statusPaid': 'Paid',
  'inv.statusVoid': 'Void',
  'inv.statusOverdue': 'Overdue',
  'inv.emptyAll': 'No invoices yet.',
  'inv.emptyFiltered': 'No {{status}} invoices.',
  'inv.colNumber': 'Number',
  'inv.colUnit': 'Unit',
  'inv.colTotal': 'Total (Rp)',
  'inv.colIssued': 'Issued',
  'inv.colDue': 'Due',
  'inv.paidAmount': '{{amount}} paid',
  'inv.loadMore': 'Load more',
  'common.loadingEllipsis': 'Loading…',
  'common.cancel': 'Cancel',
  'common.saving': 'Saving…',
  'inv.backToList': 'Back to invoices',
  'inv.notFound': 'Invoice not found.',
  'inv.sourceLabel': 'Source: {{source}}',
  'inv.markPaid': 'Mark paid',
  'inv.void': 'Void',
  'inv.detailsSection': 'Details',
  'inv.unitId': 'Unit ID',
  'inv.paidAt': 'Paid at',
  'inv.voidedAt': 'Voided at',
  'inv.voidReason': 'Void reason',
  'inv.notes': 'Notes',
  'inv.amountsSection': 'Amounts (Rp)',
  'inv.subtotal': 'Subtotal',
  'inv.tax': 'Tax',
  'inv.total': 'Total',
  'inv.paid': 'Paid',
  'inv.outstanding': 'Outstanding',
  'inv.lineItems': 'Line items',
  'inv.colDescription': 'Description',
  'inv.colQty': 'Qty',
  'inv.colUnitPrice': 'Unit price (Rp)',
  'inv.noLineItems': 'No line items.',
  'inv.markPaidTitle': 'Mark invoice paid',
  'inv.amountPositive': 'Amount must be a positive number.',
  'inv.recordedPaid': 'Recorded Rp {{amount}} paid. Residents notified.',
  'inv.paymentMethod': 'Payment method',
  'inv.bankTransfer': 'Bank transfer',
  'inv.cash': 'Cash',
  'inv.amount': 'Amount',
  'inv.outstandingAmount': 'Outstanding: Rp {{amount}}',
  'inv.noteOptional': 'Note (optional)',
  'inv.notePlaceholder': 'e.g. transfer ref 20260522-BCA-XXXX',
  'inv.confirmPayment': 'Confirm payment',
  'inv.voidTitle': 'Void invoice',
  'inv.reasonRequired': 'A reason is required.',
  'inv.invoiceVoided': 'Invoice voided.',
  'inv.voidWarning': 'Voiding hides the invoice from residents and prevents payment. It cannot be undone.',
  'inv.reason': 'Reason',
  'inv.reasonPlaceholder': 'e.g. duplicate of INV-202605-ABCDEF01',
  'inv.voiding': 'Voiding…',
  // create
  'inv.createTitle': 'New invoice',
  'inv.createSubtitle': 'Manual charge for a single unit. For monthly IPL fee schedules, use the cron-generated batch (Phase 6d).',
  'inv.createUnitsError': "Couldn't load units: {{error}}",
  'inv.createFieldUnit': 'Unit',
  'inv.createFieldDesc': 'Description',
  'inv.createDescHelper': 'Appears as the single line item on the invoice.',
  'inv.createFieldTax': 'Tax (optional)',
  'inv.createTaxHelper': 'Adds to the invoice total.',
  'inv.createFieldIssueDate': 'Issue date',
  'inv.createFieldDueDate': 'Due date',
  'inv.createFieldNotes': 'Notes (optional)',
  'inv.createNotesHelper': 'Visible to the resident on the invoice detail.',
  'inv.creating': 'Creating…',
  'inv.createSubmit': 'Create invoice',
  'inv.createValidUnit': 'Select a unit.',
  'inv.createValidDesc': 'Description is required.',
  'inv.createValidDueDate': 'Due date is required.',
  'inv.createValidTax': 'Tax must be zero or a positive number.',
  // auth (login.tsx)
  'auth.brand': 'ResidenOne',
  'auth.subtitle': 'Cluster admin panel',
  'auth.emailRequired': 'Email required',
  'auth.password': 'Password',
  'auth.passwordRequired': 'Password required',
  'auth.signingIn': 'Signing in…',
  'auth.signIn': 'Sign in',
  'auth.staffOnly': 'For cluster staff only. Residents use the mobile app.',
  'auth.loginFailed': 'Login failed',
  // common additions
  'common.email': 'Email',
  'common.status': 'Status',
  'common.actions': 'Actions',
  'common.colName': 'Name',
  'common.done': 'Done',
  'common.loadMore': 'Load more',
  'common.all': 'All',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.deleting': 'Deleting…',
  'common.fullName': 'Full name',
  'common.save': 'Save',
  'common.create': 'Create',
  // residents (residents/list.tsx)
  'res.title': 'Residents',
  'res.titleBoth': 'Residents & management team',
  'res.subtitleShort': 'Everyone who signed up in this cluster. Residents stay pending until they enter their unit invite code.',
  'res.subtitleTeam': 'The admins, staff and security who run this cluster.',
  'res.tabResidents': 'Residents',
  'res.tabTeam': 'Management team',
  'res.invite': 'Invite',
  'res.import.tab': 'Import residents',
  'res.import.step1Help': 'Fill in one resident per row: name, email and their unit. You can delete the examples in it.',
  'res.import.fileColumns': 'No "Nama lengkap", "Email" or "Nomor" column found. Use the template so the column headers match.',
  'res.import.run': 'Invite {{count}} residents',
  'res.import.sent': '{{count}} invitations sent.',
  'res.import.errName': 'Name is empty',
  'res.import.errNameLong': 'Name is longer than {{max}} characters',
  'res.import.errEmail': 'Not a valid email',
  'res.import.errNoUnit': 'Unit number is empty',
  'res.import.errUnknownUnit': 'Unit {{unit}} is not in this cluster',
  'res.import.errEmailExists': 'This email is already registered',
  'res.import.errEmailDuplicate': 'Same email as row {{line}}',
  'res.filterAll': 'All',
  'res.search': 'Search name, email or unit',
  'res.showing': '{{shown}} of {{total}} residents',
  'res.colResident': 'Resident',
  'res.colJoined': 'Joined',
  'res.notLinked': 'Not linked yet',
  'nav.peopleSummary': 'Overview',
  'ppl.title': 'Residents & property overview',
  'ppl.subtitle': 'Units, occupancy and registered residents in the cluster.',
  'ppl.download': 'Download resident data',
  'ppl.kpiUnits': 'Total units',
  'ppl.kpiOccupied': 'Occupied units',
  'ppl.kpiOccupiedSub': '{{vacant}} vacant',
  'ppl.kpiResidents': 'Active residents',
  'ppl.kpiPending': '{{count}} waiting for an invite code',
  'ppl.kpiNoPending': 'Nobody waiting',
  'ppl.kpiNoResident': 'Not registered',
  'ppl.kpiNoResidentSub': 'Houses to invite to the app',
  'ppl.signupsTitle': 'Resident sign-ups, last 12 months',
  'ppl.signupsSub': '{{count}} new residents in the last 12 months.',
  'ppl.seriesJoined': 'New residents',
  'ppl.seriesTotal': 'Total registered',
  'ppl.statusTitle': 'Resident status',
  'ppl.statusTotal': 'residents',
  'ppl.blocksTitle': 'Occupancy by block',
  'ppl.blockCounts': '{{units}} units · {{residents}} residents',
  'ppl.missingTitle': 'Not registered',
  'ppl.missingAction': 'Manage residents',
  'ppl.missingHelp': 'Occupied houses without a resident account yet. Share an invite code with them.',
  'ppl.missingNone': 'Every occupied house has a registered resident.',
  'ppl.csvBlock': 'Block / Street',
  'ppl.csvNumber': 'Number',
  'ppl.csvUnit': 'Unit',
  'ppl.csvType': 'Type',
  'ppl.csvOccupancy': 'Occupancy',
  'ppl.csvName': 'Resident name',
  'ppl.csvEmail': 'Email',
  'ppl.csvStatus': 'Resident status',
  'ppl.csvPrimary': 'Primary resident',
  'ppl.csvSince': 'Registered since',
  'ppl.csvYes': 'Yes',
  'ppl.csvNo': 'No',
  'res.subtitle': 'Everyone who signed up in this cluster. Residents are Pending until they enter their unit invite code, then Active. You can suspend an account to block access.',
  'res.statusFilter': 'Status',
  'res.statusAll': 'All statuses',
  'res.statusActive': 'Active',
  'res.statusPending': 'Pending (no code yet)',
  'res.statusSuspended': 'Suspended',
  'res.total': '{{count}} total',
  'res.emptyAll': 'No residents yet. They appear here after signing up in the app.',
  'res.emptyFiltered': 'No residents match this filter.',
  'res.colUnit': 'Unit',
  'res.mainUser': 'Main user',
  'res.reactivate': 'Reactivate',
  'res.suspend': 'Suspend',
  'res.deleteTitle': 'Delete {{name}}?',
  'res.deleteWarning': 'This removes them from the roster and revokes their access. Their billing and history are kept. Use Suspend instead for a reversible block.',
  'res.chipActive': 'Active',
  'res.chipSuspended': 'Suspended',
  'res.chipPending': 'Pending',
  'res.deletedInfo': 'Resident {{name}} deleted.',
  'res.alreadyRemoved': 'That resident was already removed.',
  // units (units/list.tsx)
  'units.title': 'Units',
  'units.subtitleShort': 'Every house and unit in the cluster. Click a unit to see its details and residents.',
  'units.filterAll': 'All',
  'units.filterUnregistered': 'Not registered',
  'units.search': 'Search a unit, e.g. A-12',
  'units.searchWithResidents': 'Search a unit or resident name',
  'units.blockAll': 'All blocks',
  'units.blockStats': '{{units}} units · {{occupied}} occupied · {{residents}} residents',
  'units.viewLabel': 'View',
  'units.viewMap': 'Map',
  'units.viewTable': 'Table',
  'units.noMatch': 'No units match this search or filter.',
  'units.legendRegistered': 'Occupied',
  'units.legendUnregistered': 'Not registered',
  'units.unregisteredHelp': 'Occupied, but nobody there has an account in the app yet. Shown in a pale colour.',
  'units.drawerResidents': 'Residents',
  'units.drawerBilling': 'Billing',
  'units.billThisMonth': 'This month',
  'units.billPaid': 'Paid',
  'units.billUnpaid': 'Unpaid',
  'units.billNone': 'Not billed',
  'units.arrears': 'Arrears',
  'units.arrearsValue': '{{count}} months · Rp {{amount}}',
  'units.noArrears': 'None',
  'units.arrearsSince': 'Unpaid since {{month}}',
  'units.editUnit': 'Edit unit',
  'units.more': 'More actions',
  'units.subtitle': 'Every house/unit in the cluster. Add units here, then generate an invite code per unit so residents can link their account.',
  'units.newUnit': 'New unit',
  'units.empty': 'No units yet. Add the first one to start onboarding residents.',
  'units.colBlock': 'Block / Street',
  'units.colNumber': 'Number',
  'units.colLabel': 'Label',
  'units.colType': 'Type',
  'units.colOccupancy': 'Occupancy',
  'units.deleteTitle': 'Delete unit {{label}}?',
  'units.deleteWarning': "This permanently removes the unit and can't be undone. A unit that still has residents, invoices, or other records can't be deleted until those are removed.",
  'units.deletedInfo': 'Unit {{label}} deleted.',
  'units.alreadyRemoved': 'That unit was already removed.',
  'units.editTitle': 'Edit unit',
  'units.createTitle': 'New unit',
  'units.block': 'Block / Street',
  'units.blockPlaceholder': 'A2 or Matahari',
  'units.number': 'Number',
  'units.labelOptional': 'Label (optional)',
  'units.labelPlaceholder': 'Leave blank to use the block and number',
  'units.type': 'Type',
  'units.occupancy': 'Occupancy',
  'units.blockCannotEdit': "Block/street + number are the unit's identity and can't be edited — delete and recreate if they're wrong.",
  'units.occupiedChip': 'Occupied',
  'units.vacantChip': 'Vacant',
  'units.ownerAbsentChip': 'Rented',
  'units.occHelpOccupied': 'The owner or a permanent resident lives here. This is the normal case.',
  'units.occHelpVacant': "Nobody lives here. This is a label only and does not stop invoicing; set the fee override to 0 to exempt.",
  'units.occHelpOwnerAbsent': 'Someone else lives here (a tenant or family), while the owner lives elsewhere.',
  'units.typeHouse': 'House',
  'units.typeApartment': 'Apartment',
  'units.typeRuko': 'Ruko',
  'units.colUnit': 'Unit',
  'units.tabSingle': 'One by one',
  'units.tabBulk': 'Bulk import',
  'import.step1': '1. Download the template',
  'units.import.step1Help': 'Fill in one unit per row in Excel or Google Sheets. You can delete the examples in it.',
  'import.template': 'Download template',
  'import.step2': '2. Upload the filled-in file',
  'import.step2Help': 'Keep it saved as CSV. Every row is checked before anything is added.',
  'import.choose': 'Choose CSV file',
  'import.fileEmpty': 'This file is empty. Fill in at least one row under the column headers.',
  'units.import.fileNoNumber': 'No "Nomor" column found. Use the template so the column headers match.',
  'import.summary': '{{ready}} ready to import, {{problems}} with problems',
  'import.problemsHelp': 'Rows with problems will be skipped. Fix them in the file and upload again, or go ahead with the ready ones.',
  'import.colRow': 'Row',
  'import.colStatus': 'Status',
  'import.ok': 'Ready',
  'import.otherFile': 'Choose another file',
  'units.import.run': 'Import {{count}} units',
  'import.progress': 'Working… {{done}} of {{total}}',
  'units.import.added': '{{count}} units added.',
  'import.failedCount': '{{count}} failed:',
  'import.rowFailed': 'Row {{line}} ({{what}}):',
  'import.again': 'Import another file',
  'units.import.errNumber': 'Number is empty',
  'units.import.errBlock': 'Block / street is empty',
  'units.import.errTooLong': 'Block or number is longer than {{max}} characters',
  'units.import.errLabel': 'Label is longer than {{max}} characters',
  'units.import.errType': 'Unknown type "{{value}}" (Rumah, Apartemen, Ruko)',
  'units.import.errOccupancy': 'Unknown occupancy "{{value}}" (Dihuni, Kosong)',
  'units.import.errExists': 'This unit already exists',
  'units.import.errDuplicate': 'Same as row {{line}}',
  // account dialogs (shared — users/AccountDialogs.tsx)
  'acc.creating': 'Creating…',
  'acc.create': 'Create',
  'acc.resetPassword': 'Reset password',
  'acc.removeFromCluster': 'Remove from this cluster',
  'acc.removeTitle': 'Remove {{name}} from this cluster?',
  'acc.removeWarning': "If it's their only cluster, the account is deleted.",
  'acc.removing': 'Removing…',
  'acc.remove': 'Remove',
  'acc.deleteTitle': 'Delete {{name}}?',
  'acc.deleteWarning': 'This disables the login immediately and removes it from the list.',
  'acc.editTitle': 'Edit {{name}}',
  'acc.statusActive': 'Active',
  'acc.statusSuspended': 'Suspended',
  // developer managers (users/developer-managers.tsx)
  'devmgr.title': 'Developer managers',
  'devmgr.subtitle': 'Manager logins that see every cluster under one developer. A one-time password is shown once on create.',
  'devmgr.newBtn': 'New developer manager',
  'devmgr.empty': 'No developer managers yet.',
  'devmgr.colDeveloper': 'Developer',
  'devmgr.dialogTitle': 'New developer manager',
  'devmgr.devHelper': 'Only developers that already have at least one cluster.',
  // cluster admins (users/cluster-admins.tsx)
  'cadmin.title': 'Cluster admins',
  'cadmin.subtitle': 'Pick a cluster, then manage its admin logins.',
  'cadmin.noClusters': 'No clusters available.',
  'cadmin.noClusterYet': 'No clusters yet.',
  'cadmin.adminsTitle': 'Admins · {{name}}',
  'cadmin.createBtn': 'Create cluster admin',
  'cadmin.noAdmins': 'No admins for this cluster yet.',
  'cadmin.manages': 'manages {{count}} clusters',
  'cadmin.dialogTitle': 'New cluster admin · {{cluster}}',
  'cadmin.adding': 'Adding…',
  'cadmin.add': 'Add',
  'team.title': 'Management team',
  'team.developer': 'Developer',
  'team.cluster': 'Cluster',
  'team.allClusters': 'All clusters',
  'team.createClusterFirst': 'Create a developer and cluster before adding a management team.',
  'team.assignTitle': 'Invite a resident or team member',
  'team.assignClusters': 'Managed clusters',
  'team.assignCluster': 'Assign to cluster',
  'team.companyAll': 'Company admins access all {{count}} clusters under this developer, including new clusters.',
  'team.companyAdmin': 'Company admin',
  'team.clusterAdmin': 'Cluster admin',
  'team.platformAdmin': 'Platform admin',
  'team.staff': 'Staff',
  'team.security': 'Security',
  'team.status': 'Status',
  'team.remove': 'Remove',
  'team.noMembers': 'No management team yet.',
  'team.assignedNotice': '{{email}} was assigned to {{count}} clusters.',
  'team.invitedNotice': 'Clerk invitation sent to {{email}}.',
  'team.removedDeveloperNotice': '{{name}} was removed from this developer.',
  'team.removedClusterNotice': '{{name}} was removed from this cluster.',
  'team.fullName': 'Full name',
  'team.email': 'Email address',
  'team.role': 'Role',
  'team.admin': 'Cluster admin — full access to assigned clusters',
  'team.internal': 'Internal team — selected pages',
  'team.resident': 'Resident — resident app access',
  'team.pageAccess': 'Page access',
  'team.unit': 'Unit',
  'team.block': 'Block / Street',
  'team.unitNumber': 'Unit number',
  'team.send': 'Send invitation',
  'team.sending': 'Sending invitation…',
  'team.success': 'Clerk invitation sent to {{email}} as {{role}}.',
  'team.perm.staff': 'Manage team',
  'team.perm.units': 'Manage units',
  'team.perm.residents': 'Manage residents',
  'team.perm.invites': 'Manage resident invite codes',
  'team.perm.announcements': 'Post announcements',
  'team.perm.facilities': 'Manage facilities',
  'team.perm.bookings': 'Approve bookings',
  'team.perm.payments': 'Manage payments and reports',
  'team.perm.complaints': 'Manage complaints',
  'team.perm.emergency': 'View emergency operations',
  // invite codes (users/residents.tsx)
  'invite.title': 'Residents',
  'invite.subtitle': 'Pick a cluster, then create or revoke resident invite codes (one active code per unit).',
  'invite.codesTitle': 'Invite codes · {{name}}',
  'invite.createBtn': 'Create code',
  'invite.empty': 'No invite codes for this cluster yet.',
  'invite.colCode': 'Code',
  'invite.colUses': 'Uses',
  'invite.revoke': 'Revoke',
  'invite.createdTitle': 'Invite code created',
  'invite.shareNote': 'Share it with the resident — it expires in 24 hours.',
  'invite.dialogTitle': 'New invite code · {{cluster}}',
  'invite.maxUses': 'Max uses',
  'invite.maxUsesHelper': 'How many accounts may register with this code (default 2).',
  'invite.noUnits': 'Every unit already has an active code, or this cluster has no units yet. Add a unit first.',
  // security (users/security.tsx)
  'sec.title': 'Security',
  'sec.subtitle': 'Pick a cluster, then manage its security-guard logins.',
  'sec.sectionTitle': 'Security · {{name}}',
  'sec.createBtn': 'Create security account',
  'sec.empty': 'No security accounts for this cluster yet.',
  'sec.dialogTitle': 'New security account · {{cluster}}',
  // bookings (bookings/list.tsx + status-chip.tsx)
  'book.title': 'Booking queue',
  'book.subtitle': 'Approve or reject resident requests. Auto-confirm facilities never appear in Pending.',
  'book.emptyAll': 'No bookings yet.',
  'book.emptyStatus': 'No {{status}} bookings.',
  'book.colFacility': 'Facility',
  'book.colWhen': 'When',
  'book.colUnit': 'Unit',
  'book.approve': 'Approve',
  'book.reject': 'Reject',
  'book.approvedMsg': 'Approved booking on {{facility}}.',
  'book.rejectTitle': 'Reject booking',
  'book.rejectInfo': 'The resident will see this reason. Be specific so they can reschedule.',
  'book.reason': 'Reason',
  'book.reasonPlaceholder': 'e.g. Facility under maintenance that day',
  'book.reasonRequired': 'A reason is required.',
  'book.rejecting': 'Rejecting…',
  'book.rejectBtn': 'Reject booking',
  'book.rejectedMsg': 'Rejected booking on {{facility}}.',
  'book.statusPending': 'Pending',
  'book.statusConfirmed': 'Confirmed',
  'book.statusCancelled': 'Cancelled',
  'book.statusRejected': 'Rejected',
  'book.statusCompleted': 'Completed',
  // complaints (complaints/chips.tsx + list.tsx + show.tsx)
  'comp.title': 'Complaints',
  'comp.subtitle': 'Resident reports — both public and private. Move them through In progress and Resolved (resolution requires evidence photo).',
  'comp.filterCategory': 'Category',
  'comp.filterVisibility': 'Visibility',
  'comp.filterBlock': 'Block / Street',
  'comp.thisMonth': 'This month',
  'comp.sortLabel': 'Sort',
  'comp.sortNewest': 'Newest first',
  'comp.sortOldest': 'Oldest first',
  'comp.empty': 'No complaints match those filters.',
  'comp.colTitle': 'Title',
  'comp.colBlockUnit': 'Unit',
  'comp.colVisibility': 'Visibility',
  'comp.colObserved': 'Observed',
  'comp.colFiled': 'Filed',
  'comp.statusNew': 'New',
  'comp.statusInProgress': 'In progress',
  'comp.statusResolved': 'Resolved',
  'comp.statusCancelled': 'Cancelled',
  'comp.catFacilityDamage': 'Facility damage',
  'comp.catCleanliness': 'Cleanliness',
  'comp.catNoise': 'Noise',
  'comp.catLighting': 'Lighting',
  'comp.catSecurity': 'Security',
  'comp.catOther': 'Other',
  'comp.backToList': 'Back to complaints',
  'comp.notFound': 'Complaint not found.',
  'comp.moveToInProgress': 'Move to In progress',
  'comp.markResolved': 'Mark resolved',
  'comp.visibilityLine': 'Visibility: {{vis}} · Observed {{observed}} · Filed {{filed}}',
  'comp.detailsSection': 'Details',
  'comp.location': 'Location',
  'comp.reportedBy': 'Reported by',
  'comp.inProgressAt': 'In progress at',
  'comp.resolvedAt': 'Resolved at',
  'comp.cancelledAt': 'Cancelled at',
  'comp.resolutionNotes': 'Resolution notes',
  'comp.residentPhoto': 'Resident photo',
  'comp.noResidentPhoto': 'No photo uploaded by the resident.',
  'comp.resolutionPhoto': 'Resolution photo',
  'comp.notResolved': 'Not yet resolved.',
  'comp.resolveTitle': 'Mark resolved',
  'comp.r2Warning': "R2 storage isn't configured yet — saved the photo inline. In production, provision the FILES binding to switch to real R2 storage.",
  'comp.resolveInfo': 'Upload a photo as evidence. The resident will see this on their detail page.',
  'comp.pickPhoto': 'Choose photo',
  'comp.selectedPhoto': 'Selected: {{name}}',
  'comp.notesOptional': 'Notes (optional)',
  'comp.notesPlaceholder': 'e.g. replaced broken light bulb, tested both switches',
  'comp.resolving': 'Resolving…',
  'comp.markedInProgress': 'Marked in progress. Resident notified.',
  'comp.markedResolved': 'Marked resolved. Resident notified.',
  'comp.noPhoto': 'Pick a resolution photo to upload.',
  // facilities (facilities/*.tsx)
  'fac.title': 'Facilities',
  'fac.subtitle': 'Bookable amenities for your cluster. Toggle approval to control auto-confirm vs queue.',
  'fac.newFacility': 'New facility',
  'fac.empty': 'No facilities yet.',
  'fac.addFirst': 'Add the first one',
  'fac.colHours': 'Hours',
  'fac.colSlot': 'Slot',
  'fac.colApproval': 'Approval',
  'fac.hours24h': '24h',
  'fac.approvalRequired': 'Required',
  'fac.approvalAuto': 'Auto-confirm',
  'fac.walkIn': 'Walk-in',
  'fac.statusActive': 'Active',
  'fac.statusHidden': 'Hidden',
  'fac.backToList': 'Back to facilities',
  'fac.createTitle': 'New facility',
  'fac.createSubtitle': 'Define an amenity residents can book. Slot length controls how granular booking choices are.',
  'fac.editTitle': 'Edit facility',
  'fac.notFound': 'Facility not found. It may have been deleted; go back to the list and try again.',
  'fac.saveChanges': 'Save changes',
  'fac.fieldName': 'Name',
  'fac.nameHelper': 'e.g. Padel Court 1',
  'fac.fieldType': 'Type',
  'fac.fieldDesc': 'Description (optional)',
  'fac.fieldCapacity': 'Capacity (optional)',
  'fac.capacityHelper': 'People per slot',
  'fac.fieldOpenTime': 'Open time',
  'fac.timeHelper': 'Leave empty for 24h',
  'fac.fieldCloseTime': 'Close time',
  'fac.fieldSlot': 'Slot length',
  'fac.fieldImageUrl': 'Image URL (optional)',
  'fac.imageHelper': "Shown as the facility's cover on mobile. Max 3 MB (JPG/PNG/WebP).",
  'fac.fieldImage': 'Facility image (optional)',
  'fac.imageUpload': 'Upload image',
  'fac.imageReplace': 'Replace image',
  'fac.imageRemove': 'Remove',
  'fac.imageBadType': 'Image must be JPG, PNG, or WebP.',
  'fac.imageTooBig': 'Image must be 3 MB or smaller.',
  'fac.requiresBooking': 'Requires booking',
  'fac.bookingHelper': 'When off, the facility is walk-in: residents just come during open hours, with no slots or bookings (e.g. a pool).',
  'fac.requiresApproval': 'Bookings require staff approval',
  'fac.approvalHelper': 'When off, bookings auto-confirm. When on, they enter the approval queue.',
  'fac.isActive': 'Active (residents can book this facility)',
  'fac.validName': 'Name is required.',
  'fac.validSlot': 'Slot length must be at least 5 minutes.',
  'fac.validTimes': 'Set both open and close times, or leave both empty.',
  'fac.validCapacity': 'Capacity must be a positive number.',
  'fac.submitCreate': 'Create facility',
  'fac.saveFailed': 'Save failed.',
  'fac.typePadel': 'Padel',
  'fac.typeTennis': 'Tennis',
  'fac.typeBasketball': 'Basketball',
  'fac.typeGym': 'Gym',
  'fac.typePool': 'Pool',
  'fac.typeMeeting': 'Meeting room',
  'fac.typeBbq': 'BBQ area',
  'fac.typeOther': 'Other',
  // platform — dashboard (platform/dashboard.tsx)
  'plt.overviewTitle': 'Platform overview',
  'plt.overviewSubtitle': 'Live totals across client companies and their property clusters.',
  'plt.tileDevelopers': 'Developers',
  'plt.tileArchived': '{{count}} archived',
  'plt.tileClusters': 'Clusters',
  'plt.tileUnits': 'Units',
  'plt.tileUnitsInActive': 'in active clusters',
  'plt.tileOccupied': 'Occupied units',
  'plt.tileOccupiedPct': '{{pct}}% · all clusters',
  'plt.tileResidents': 'Residents',
  'plt.tileResidentsSub': 'linked accounts',
  'plt.tileAdmins': 'Admin accounts',
  'plt.tileAdminsSub': 'company + cluster admins',
  'plt.tileSecurity': 'Security',
  'plt.tileSecuritySub': 'guard logins',
  'plt.tileInviteCodes': 'Active invite codes',
  'plt.tileInviteCodesSub': 'pending onboarding',
  'plt.occupancyTitle': 'Occupancy',
  'plt.allDevelopers': 'All developers',
  'plt.allClusters': 'All clusters',
  'plt.occupied': 'occupied',
  'plt.scopeAll': 'all clusters (total)',
  'plt.scopeDev': 'developer total',
  'plt.scopeCluster': 'selected cluster',
  'plt.chartTitle': 'Occupied / total units per developer',
  'plt.noDevs': 'No developers yet.',
  // platform — developers (platform/developers.tsx)
  'plt.devTitle': 'Developers',
  'plt.devSubtitle': 'Each client developer can own several property clusters. Invite its admin through Residents › Management team.',
  'plt.newDev': 'New developer',
  'plt.noDevYet': 'No client developers yet. Create the first one.',
  'plt.noClusters': 'No clusters.',
  'plt.archiveDevTitle': 'Archive {{name}}?',
  'plt.archiveDevWarning': 'It and its clusters go offline now and are permanently deleted in 30 days. You can restore it from the Archived page until then.',
  'plt.archiving': 'Archiving…',
  'plt.archive': 'Archive',
  'plt.newDevTitle': 'New client developer',
  'plt.newDevNote': 'Create the developer, then add its clusters and invite an admin.',
  'plt.devNameField': 'Developer company name',
  'plt.legacyName': 'Legacy property without a company',
  'plt.legacyBadge': 'Needs assignment',
  'plt.legacyHelp': 'Older data from an early migration. Assign its real company in Clusters → Edit → Advanced. Property:',
  'plt.legacyHelpInClusters': 'Older property data. Use Edit → Advanced to assign the correct client company.',
  // platform — clusters (platform/clusters.tsx)
  'plt.clusterTitle': 'Clusters',
  'plt.clusterSubtitle': 'Property clusters grouped by client company.',
  'plt.newCluster': 'New cluster',
  'plt.noDevFirst': 'Create a client company first, then add its property clusters.',
  'plt.noClusterYet': 'No clusters yet.',
  'plt.colCluster': 'Cluster',
  'plt.colType': 'Type',
  'plt.colLocation': 'Location',
  'plt.colSlug': 'Slug',
  'plt.tooltipEditContact': 'Edit contact info',
  'plt.archiveClusterTitle': 'Archive {{name}}?',
  'plt.archiveClusterWarning': 'It goes offline now and is permanently deleted in 30 days — restore from the Archived page until then.',
  'plt.newClusterTitle': 'New cluster',
  'plt.devField': 'Client company',
  'plt.clusterDetailsOpt': 'Cluster details (optional)',
  'plt.fieldAddress': 'Address',
  'plt.fieldCity': 'City',
  'plt.fieldProvince': 'Province',
  'plt.fieldPostalCode': 'Postal code',
  'plt.fieldPhone': 'Contact phone',
  'plt.fieldContactEmail': 'Contact email',
  'plt.fieldClusterName': 'Cluster name',
  'plt.fieldPropertyType': 'Property type',
  'plt.propertyTypeHousing': 'House Resident',
  'plt.propertyTypeApartment': 'Apartment',
  'plt.editClusterTitle': 'Edit {{name}}',
  'plt.contactSection': 'Contact / record details',
  'plt.moreBtn': 'More',
  'plt.advancedSection': 'Advanced — name & company',
  'plt.devMoveHelper': 'Moves this cluster to another company and updates company admin access.',
  'plt.clusterCount': '{{count}} cluster',
  // platform — archived (platform/archived.tsx)
  'plt.archivedTitle': 'Archived',
  'plt.archivedSubtitle': 'Archived companies and clusters are offline and will be permanently deleted after {{days}} days. Restore to bring them back.',
  'plt.archiveDevSection': 'Companies',
  'plt.archiveClusterSection': 'Clusters',
  'plt.archiveNone': 'None archived.',
  'plt.colDeletesIn': 'Deletes in',
  'plt.colAction': 'Action',
  'plt.restore': 'Restore',
  'plt.daysLeft': '{{days}} days',
  // platform — CredentialsDialog (platform/CredentialsDialog.tsx)
  'plt.credTitle': 'Account created',
  'plt.credNote': 'Hand these over now — the password is shown only once.',
  'plt.credPassword': 'Temporary password',
  // reminders (reminders/list.tsx)
  'rem.title': 'Payment reminders',
  'rem.subtitle': 'Send a payment nudge to residents in this cluster.',
  'rem.tileUnitsOwe': 'Units that owe',
  'rem.tileTotalOutstanding': 'Total outstanding',
  'rem.tileLastSent': 'Last reminder sent',
  'rem.recipientsLabel': 'Recipients',
  'rem.scopeOwing': 'Units that owe',
  'rem.scopeAll': 'All units',
  'rem.messageField': 'Message',
  'rem.messageHelper': 'Each resident also sees their own line, e.g. "Unit B-12: outstanding Rp 350.000, due 25 Jun 2026."',
  'rem.colOutstanding': 'Outstanding',
  'rem.colDue': 'Due',
  'rem.noMainUser': 'no Main user — skipped',
  'rem.sendBtn': 'Send reminder to {{count}} resident{{plural}}',
  'rem.emptyOwing': 'No units currently owe — nothing to send.',
  'rem.emptyAll': 'No units in this cluster.',
  'rem.confirmTitle': 'Send payment reminder?',
  'rem.confirmText': 'This will notify {{count}} resident{{plural}} (the Main user of each {{scope}}).',
  'rem.scopeOwingLabel': 'unit that owes',
  'rem.scopeAllLabel': 'unit',
  'rem.sending': 'Sending…',
  'rem.send': 'Send',
  // operations (operations/list.tsx)
  'ops.title': 'Operations',
  'ops.subtitle': 'Read-only logs of gate activity across the cluster — visitor check-ins, packages at the guard post, and emergency alerts.',
  'ops.tabVisitors': 'Visitor check-ins',
  'ops.tabPackages': 'Packages',
  'ops.tabEmergency': 'Emergency alerts',
  'ops.emptyVisitors': 'No visitor check-ins yet.',
  'ops.emptyPackages': 'No packages logged yet.',
  'ops.emptyEmergency': 'No emergency alerts — good news.',
  'ops.colWhen': 'When',
  'ops.colVisitor': 'Visitor',
  'ops.colHost': 'Host',
  'ops.colScannedBy': 'Scanned by',
  'ops.colRecipient': 'Recipient',
  'ops.colCarrier': 'Carrier',
  'ops.colKind': 'Kind',
  'ops.colRaisedBy': 'Raised by',
  'ops.pkgPickedUp': 'Picked up',
  'ops.pkgCancelled': 'Cancelled',
  'ops.pkgWaiting': 'Waiting',
  'ops.alertResolved': 'Resolved',
  'ops.alertCancelled': 'Cancelled',
  'ops.alertActive': 'Active',
  // branding (resident-app/branding.tsx)
  'brand.title': 'Resident app — Branding',
  'brand.subtitle': 'The cover photo shown at the top of the resident app home screen for {{name}}. Use a wide landscape image (about 16:10) — it is cropped to fill.',
  'brand.tooBig': 'That image is too large — please choose a file under 5 MB.',
  'brand.updated': 'Hero image updated',
  'brand.reverted': 'Reverted to the default image',
  'brand.useDefault': "Using the app's default image",
  'brand.working': 'Working…',
  'brand.upload': 'Upload image',
  'brand.remove': 'Remove (use default)',
  // change password dialog (components/ChangePasswordDialog.tsx)
  'pwd.title': 'Change password',
  'pwd.changed': 'Your password has been changed.',
  'pwd.current': 'Current password',
  'pwd.new': 'New password',
  'pwd.tooShort': 'At least {{min}} characters',
  'pwd.confirm': 'Confirm new password',
  'pwd.mismatch': "Passwords don't match",
  'pwd.changing': 'Saving…',
  'pwd.changeBtn': 'Change password',
  // edit profile dialog (components/EditProfileDialog.tsx)
  'prof.title': 'Edit profile',
  'prof.nameLabel': 'Display name',
  'prof.nameRequired': 'Name is required',
  'prof.upload': 'Upload photo',
  'prof.replace': 'Replace photo',
  'prof.uploading': 'Uploading…',
  'prof.remove': 'Remove photo',
  'prof.tooBig': 'Image must be at most {{max}} MB',
  'prof.photoHint': 'JPG, PNG, or WebP. Max {{max}} MB.',
  'prof.save': 'Save',
  'prof.saving': 'Saving…',
  // cluster switcher (components/ClusterSwitcher.tsx)
  'cluster.fallback': 'Cluster',
  'cluster.switchAriaLabel': 'switch cluster',
  // notification bell (components/NotificationBell.tsx)
  'notif.title': 'Notifications',
  'notif.empty': 'Nothing needs attention right now.',
  'notif.emergencies': 'Active emergency alerts',
  'notif.bookingsPending': 'Bookings to approve',
  'notif.complaintsNew': 'New complaints',
  'notif.packagesNew': 'New packages',
  'notif.scansNew': 'New visitor check-ins',
  // announcements (announcements/*.tsx)
  'ann.title': 'Announcements',
  'ann.subtitle': 'Outages, maintenance, security notes for your residents.',
  'ann.newBtn': 'New announcement',
  'ann.empty': 'No announcements yet.',
  'ann.composeFirst': 'Compose the first one',
  'ann.colTitle': 'Title',
  'ann.colCategory': 'Category',
  'ann.colStatus': 'Status',
  'ann.colPublished': 'Published',
  'ann.requiresAck': 'Requires ack',
  'ann.statusPublished': 'Published',
  'ann.statusDraft': 'Draft',
  'ann.publishedMsg': 'Published. {{count}} resident(s) notified.',
  'ann.deleteConfirm': 'Delete "{{title}}"? This cannot be undone.',
  'ann.fieldCategory': 'Category',
  'ann.categoryRequired': 'Pick a category',
  'ann.categoryHelper': 'Drives the push-notification preview color on the resident app.',
  'ann.fieldTitle': 'Title',
  'ann.titleRequired': 'Title required',
  'ann.titleMaxLen': 'Max 200 chars',
  'ann.titleHelper': 'Shown on the push and as the first line in the resident inbox.',
  'ann.fieldBody': 'Body (Markdown supported)',
  'ann.bodyRequired': 'Body required',
  'ann.bodyHelper': "Tip: keep the first ~140 chars informative — that's the push preview.",
  'ann.pinToTop': 'Pin to top of list',
  'ann.requireAck': 'Require residents to acknowledge',
  'ann.publishNow': 'Publish + notify residents now',
  'ann.saveDraft': 'Uncheck to save as a draft you can publish later.',
  'ann.saveFailed': 'Failed to save',
  'ann.saveChanges': 'Save changes',
  'ann.createBtn': 'Create',
  'ann.publishedNotif': 'Published! {{count}} resident(s) notified.',
  'ann.savedDraft': 'Saved as draft.',
  'ann.saved': 'Saved.',
  'ann.catOutage': 'Outage',
  'ann.catMaintenance': 'Maintenance',
  'ann.catGeneral': 'General',
  'ann.catSecurity': 'Security',
  // common additions (shared across cluster-picker pages)
  'common.close': 'Close',
  // billing fees (billing/fees.tsx)
  'fee.title': 'Fees',
  'fee.subtitle': 'Set the monthly fee every unit pays. The invoice cron issues it each month from the start month onward. Add per-unit overrides for units that pay a different amount or are exempt.',
  'fee.saved': 'Monthly fee saved.',
  'fee.monthlyFee': 'Monthly fee',
  'fee.amount': 'Amount',
  'fee.amountHelper': 'What every unit pays each month (before overrides).',
  'fee.effectiveFrom': 'Effective from',
  'fee.effectiveFromHelper': 'First month billed.',
  'fee.dueDay': 'Due day',
  'fee.dueDayHelper': 'Day of month (1–28).',
  'fee.saveFee': 'Save fee',
  'fee.setupFee': 'Set up monthly fee',
  'fee.overridesTitle': 'Per-unit overrides',
  'fee.addOverride': 'Add override',
  'fee.setFeeFirst': 'Set the monthly fee first, then add overrides.',
  'fee.noOverrides': 'No overrides — every unit pays the monthly fee.',
  'fee.colUnit': 'Unit',
  'fee.colPays': 'Pays',
  'fee.exempt': 'Exempt',
  'fee.removeTooltip': 'Remove (revert to monthly fee)',
  'fee.editOverride': 'Edit override',
  'fee.exemptCheckbox': 'Exempt (pays nothing)',
  'fee.overrideAmountHelper': 'What this unit pays each month instead of the cluster fee.',
  // segment input (components/SegmentInput.tsx)
  'seg.typeAlpha': 'Alpha',
  'seg.typeNum': 'Number',
  'seg.typeCustom': 'Custom',
  'seg.customPlaceholder': 'free text',
  'seg.removeSegment': 'Remove segment',
  'seg.addSegment': 'Add segment',
  // platform — cluster address scheme (platform/clusters.tsx)
  'plt.addrSchemeTitle': 'Unit address scheme',
  'plt.addrSchemeHelper': 'Number is always required. Toggle the other parts this cluster uses.',
  'plt.addrStreet': 'Street number',
  'plt.addrBlock': 'Block',
  // units — residents dialog (units/list.tsx)
  'units.subtitleResidentsHint': 'Click a unit to see its residents and set the primary.',
  'units.residentsTitle': 'Residents · {{label}}',
  'units.residentsHelper': 'The primary resident receives billing emails. Use the star to set a different primary.',
  'units.noResidentsLinked': 'No residents linked to this unit yet. Generate an invite code so a resident can sign up.',
  'units.colPrimary': 'Primary',
  'units.primaryChip': 'Primary',
  'units.primaryTooltip': 'Primary resident',
  'units.makePrimary': 'Make primary',
  // invite codes — new dialogs + segment flow (users/residents.tsx)
  'invite.clustersChip': '{{count}} cluster{{plural}}',
  'invite.colExpiresIn': 'Expires in',
  'invite.deleteCodeTooltip': 'Delete this code permanently',
  'invite.deleteCodeTitle': 'Delete code {{code}}?',
  'invite.deleteCodeWarningUnit': "This permanently removes the code for {{unit}}. It can't be undone — generate a new code if the resident still needs to sign up.",
  'invite.deleteCodeWarning': "This permanently removes the code. It can't be undone — generate a new code if the resident still needs to sign up.",
  'invite.buildAddress': 'Build the unit address. The code lets up to 3 accounts sign up and expires in 24 hours.',
  'invite.segStreet': 'Street number',
  'invite.segBlock': 'Block',
  'invite.segNumber': 'Number',
  'invite.unitPrefix': 'Unit',
  'invite.existingCodeTitle': 'This unit already has a code',
  'invite.existingCodeBody': "Share the existing code with the resident — it's still valid. Or replace it with a new one (the old code stops working).",
  'invite.codeUsed': 'used {{used}}/{{max}}',
  'invite.codeExpires': 'expires in {{expires}}',
  'invite.replaceWithNew': 'Replace with new',
  'invite.replacing': 'Replacing…',
  'invite.unitFullTitle': 'Unit already full',
  'invite.generateAnyway': 'Generate anyway',
  'invite.generating': 'Generating…',
  // cleanup additions
  'ann.editTitle': 'Edit announcement',
  'ann.publish': 'Publish',
  'ann.coverImage': 'Cover image (optional)',
  'ann.coverUpload': 'Upload image',
  'ann.coverRemove': 'Remove image',
  'ann.coverWorking': 'Uploading…',
  'ann.coverTooBig': 'Image must be under 2 MB.',
  'ann.coverBadType': 'Unsupported format. Use JPG, PNG, or WebP.',
  'comp.visPublic': 'public',
  'comp.visPrivate': 'private',
  // landing (public product-intro page)
  'landing.nav.features': 'Features',
  'landing.nav.management': 'Management',
  'landing.nav.residents': 'Residents',
  'landing.nav.contact': 'Contact',
  'landing.nav.login': 'Admin login',
  'landing.hero.kicker': 'Cluster living management',
  'landing.hero.title': 'One app for your whole residential cluster',
  'landing.hero.sub':
    'Announcements, dues, facilities, complaints, and guests, all in one place for management and residents.',
  'landing.hero.ctaLogin': 'Admin login',
  'landing.hero.ctaApp': 'Get the resident app',
  'landing.hero.imageAlt': 'Residential cluster photo',
  'landing.features.title': 'Everything you need to run a cluster, in one platform',
  'landing.features.announcements': 'Announcements',
  'landing.features.billing': 'Dues and payments',
  'landing.features.facilities': 'Facility booking',
  'landing.features.complaints': 'Resident complaints',
  'landing.features.security': 'Guests and security',
  'landing.features.reports': 'Financial reports',
  'landing.mgmt.kicker': 'For management',
  'landing.mgmt.title': 'A tidy web dashboard for cluster admins',
  'landing.mgmt.b1': 'Manage units and resident data',
  'landing.mgmt.b2': 'Generate monthly dues automatically',
  'landing.mgmt.b3': 'Track payments and arrears',
  'landing.mgmt.b4': 'Manage staff and security',
  'landing.mgmt.b5': 'View financial reports',
  'landing.mgmt.cta': 'Admin login',
  'landing.res.kicker': 'For residents',
  'landing.res.title': 'A mobile app for residents',
  'landing.res.b1': 'View and pay dues',
  'landing.res.b2': 'Book facilities',
  'landing.res.b3': 'Report complaints',
  'landing.res.b4': 'Get announcements',
  'landing.res.b5': 'Invite guests',
  'landing.res.comingSoon': 'Coming soon',
  'landing.how.title': 'Get started in 3 steps',
  'landing.how.s1': 'Management creates the cluster and units',
  'landing.how.s2': 'Residents join with an invite code',
  'landing.how.s3': 'Dues and services run automatically',
  'landing.contact.title': 'Want ResidenOne in your cluster?',
  'landing.contact.sub': 'Get in touch for a demo.',
  'landing.contact.whatsapp': 'Chat on WhatsApp',
  'landing.contact.email': 'Email us',
  'landing.footer.rights': '© 2026 ResidenOne · PT Semesta Digital Integrasi',
};

const DICTS: Record<Lang, Dict> = { id: ID, en: EN };

export function initialLang(): Lang {
  const s = localStorage.getItem(STORAGE_KEY);
  return s === 'en' || s === 'id' ? s : 'id';
}

export type TFunc = (key: string, vars?: Record<string, string | number | undefined>) => string;

interface LanguageCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
}

const LanguageContext = createContext<LanguageCtx>({ lang: 'id', setLang: () => {}, t: (k) => k });

export const useLanguage = () => useContext(LanguageContext);
export const useT = (): TFunc => useContext(LanguageContext).t;

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const setLang = useCallback((l: Lang) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
  }, []);
  const t = useCallback<TFunc>(
    (key, vars) => {
      let s = DICTS[lang][key] ?? DICTS.id[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`{{${k}}}`, 'g'), String(v ?? ''));
        }
      }
      return s;
    },
    [lang],
  );
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
